import { useRef, useEffect, useLayoutEffect, useMemo, useCallback, useState, forwardRef, useImperativeHandle, useId } from 'react';

import { BryntumScheduler } from '@bryntum/scheduler-react';
import { Scheduler, Menu } from '@bryntum/scheduler';
import '@bryntum/scheduler/scheduler.css';
import '@/styles/bryntum-evo-theme.css';
import { useBryntumTheme } from '@/contexts/BryntumThemeContext';

import { useScheduler } from '@/contexts/SchedulerContext';
import { Resource, Booking, Absence, BankHoliday, CalendarView, FieldConfiguration, BookingType, StatusConfig, SchedulerGroup } from '@/types';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, isValid } from 'date-fns';
import { convertTimezone, getTimezoneOffset } from '@/utils/timezone-utils';
import { calculateDailyUtilization, calculatePeriodUtilization } from '@/utils/heatmap-utils';
import { getUtilizationColor, getUtilizationLabel, getUtilizationColorCapped } from '@/utils/utilization-labels';
import { getBookingColor } from '@/hooks/use-booking-color';

// Generate unique ID with timestamp and random component to avoid conflicts in React Strict Mode
const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

// Grouping mode type for timeline view
export type TimelineGroupingMode = 'resource' | 'project-resource' | 'role-resource' | 'customer';



interface BryntumSchedulerTimelineProps {
  selectedDate: Date;
  calendarView: CalendarView;
  zoomLevel: number;
  selectedTimezone?: string;
  resources: Resource[];
  bookings: Booking[];
  resourceTimeRanges?: Array<{
    id: string;
    resourceId: string;
    name: string;
    startDate: Date;
    endDate: Date;
    timeRangeColor?: string;
    cls?: string;
    style?: string;
  }>;
  timeRanges?: Array<{
    id: string;
    name: string;
    startDate: Date;
    endDate: Date;
    cls?: string;
  }>;
  groupingMode?: TimelineGroupingMode;
  schedulerGroups?: SchedulerGroup[];
  statusConfigs?: StatusConfig[];
  isHeatmapMode?: boolean;
  absences?: Absence[];
  bankHolidays?: BankHoliday[];
  searchQuery?: string;
  enableTimezones?: boolean;
  onEditBooking?: (booking: Booking) => void;
  onResolveConflict?: (bookings: Booking[]) => void;
  onDeleteBooking?: (booking: Booking) => void;
  onDuplicateBooking?: (booking: Booking) => void;
  onMoveBooking?: (booking: Booking) => void;
  onStatusChange?: (booking: Booking, newStatus: string, statusColor: string) => void;
  onCopyBooking?: (booking: Booking) => void;
  onSlotClick?: (resource: Resource, date: Date, startTime: { hour: number; minute: number }, bookingData?: Partial<Booking>) => void;
  onDragSelect?: (
    resource: Resource,
    date: Date,
    startTime: { hour: number; minute: number },
    endTime: { hour: number; minute: number },
    bookingData?: Partial<Booking>,
    endDate?: Date
  ) => void;
  onBookingDragEnd?: (
    booking: Booking,
    originalResource: Resource,
    newResourceId: string,
    newStartTime: Date,
    newEndTime: Date,
    isResize?: boolean
  ) => void;
  onAddBooking?: (resource: Resource, date: Date) => void;
  onQuickAddBooking?: (resource: Resource, date: Date) => void;
  onPasteBooking?: (resource: Resource, date: Date, startTime: { hour: number; minute: number }, bookingData?: Booking) => void;
  copiedBooking?: Booking | null;
  /** What-if scenario handed off from the heatmap — drives ghost tile rendering */
  whatIfScenario?: import('@/types').WhatIfScenario | null;
}

export interface BryntumSchedulerTimelineHandle {
  revertEvent: (bookingId: string, originalResourceId: string, originalStartTime: Date, originalEndTime: Date) => void;
  cleanupPhantomEvents: () => void;
}

interface BryntumResource {
  id: string;
  name: string;
  role?: string;
  department?: string;
  imageUrl?: string;
}

interface BryntumEvent {
  id: string;
  resourceId: string;
  name: string;
  startDate: Date;
  endDate: Date;
  eventColor?: string;
  cls?: string;
  originalBooking?: Booking;
  isHeatmapEvent?: boolean;
  isWhatIfGhost?: boolean;
  eventStyle?: string;
  heatmapData?: {
    bookedHours: number;
    availableHours: number;
    utilization: number;
    band: string;
    bandColor: string;
    bookings: Booking[];
    confirmedHours: number;
    plannedHours: number;
    absenceHours: number;
    absenceReasons: string[];
    hasHardConflict: boolean;
    weeklyAvgUtilization: number | null;
    /** True when ALL bookings in this cell are non-confirmed (planned/tentative) */
    isPlannedOnly: boolean;
    /** True when the cell has BOTH confirmed and planned bookings */
    isMixed: boolean;
    /**
     * Ratio of planned hours to total booked hours (0–1).
     * 0 = fully confirmed, 1 = fully planned, 0.6 = 60% planned.
     */
    plannedRatio: number;
  };
}

export const BryntumSchedulerTimeline = forwardRef<BryntumSchedulerTimelineHandle, BryntumSchedulerTimelineProps>(({
  selectedDate,
  calendarView,
  zoomLevel,
  selectedTimezone = 'UTC',
  resources,
  bookings,
  resourceTimeRanges = [],
  timeRanges = [],
  groupingMode = 'resource',
  schedulerGroups = [],
  statusConfigs = [],
  isHeatmapMode = false,
  absences = [],
  bankHolidays = [],
  searchQuery = '',
  enableTimezones = false,
  onEditBooking,
  onResolveConflict,
  onDeleteBooking,
  onMoveBooking,
  onStatusChange,
  onCopyBooking,
  onSlotClick,
  onDragSelect,
  onBookingDragEnd,
  onAddBooking,
  onQuickAddBooking,
  onPasteBooking,
  copiedBooking,
  whatIfScenario,
}, ref) => {
  // SAFETY CHECK: Ensure selectedDate is always valid
  const safeSelectedDate = useMemo(() => {
    if (selectedDate instanceof Date && !isNaN(selectedDate.getTime())) {
      return selectedDate;
    }
    console.warn('Invalid selectedDate received, using current date:', selectedDate);
    return new Date();
  }, [selectedDate]);

  const schedulerRef = useRef<BryntumScheduler>(null);
  const initialDatesRef = useRef({ startDate: safeSelectedDate, endDate: safeSelectedDate });
  // Track whether the initial computed dates have been applied to the ref.
  // After the first valid startDate/endDate are set, we stop updating the ref
  // so the BryntumScheduler wrapper never sees date prop changes. All subsequent
  // date updates are handled imperatively via setTimeSpan() in effects, which
  // prevents the "Invalid start/end dates" error caused by the wrapper applying
  // startDate and endDate as individual prop changes during large date jumps
  // (e.g. clicking "Today" after navigating weeks into the future).
  const hasSetInitialDates = useRef(false);
  // Populated on first render (after viewPreset useMemo) — see the assignment
  // below the viewPreset declaration.  Typed as any to avoid a forward-ref
  // initialisation order problem.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initialViewPresetRef = useRef<any>(null);
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(true);


  // Generate a stable unique ID for the scheduler instance (does not change with groupingMode).
  // Switching grouping modes updates the resource/event stores in-place via a dedicated effect.
  const schedulerIdRef = useRef<string | undefined>(undefined);
  if (!schedulerIdRef.current) {
    schedulerIdRef.current = `bryntum-scheduler-${generateUniqueId()}`;
  }
  const schedulerId = schedulerIdRef.current;

  // In heatmap mode, include calendarView in the React key so the Bryntum
  // component fully remounts when the user switches views (Week → Month, etc.).
  // A remount is the only 100%-reliable way to apply a new viewPreset — trying
  // to call setViewPreset imperatively on the live instance always races against
  // Bryntum's React wrapper which processes prop changes on its own cycle.
  // In normal (non-heatmap) mode the key stays stable so drag/edit state is
  // preserved across date navigation.
  const prevHeatmapViewRef = useRef<string>(`${String(isHeatmapMode)}-${calendarView}`);
  const schedulerKey = isHeatmapMode
    ? `${schedulerId}-heatmap-${calendarView}`
    : schedulerId;

  // When the key changes (i.e. heatmap view switch causes remount), reset the
  // one-shot refs so the new instance picks up correct initial dates and preset.
  const currentHeatmapView = `${String(isHeatmapMode)}-${calendarView}`;
  if (currentHeatmapView !== prevHeatmapViewRef.current) {
    prevHeatmapViewRef.current = currentHeatmapView;
    hasSetInitialDates.current = false;
    initialViewPresetRef.current = null;
  }

  // Cleanup effect to destroy scheduler instance on unmount
  // Using useLayoutEffect for synchronous cleanup before next render
  useLayoutEffect(() => {
    // Check if an instance with this ID already exists and destroy it
    const existingWidget = (window as any).bryntum?.query?.(schedulerId);
    if (existingWidget && !existingWidget.isDestroyed) {
      console.warn(`Destroying existing scheduler with ID: ${schedulerId}`);
      existingWidget.destroy();
    }

    return () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (scheduler && !scheduler.isDestroyed) {
        scheduler.destroy();
      }
    };
  }, [schedulerId]);



  const { settings, projects, projectDetails, clients, costCentres } = useScheduler();
  const { isThemeLoaded } = useBryntumTheme();

  // Helper function to check if a specific day is a non-working day for a resource
  const isNonWorkingDay = useCallback((resource: Resource, date: Date): boolean => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayKey = dayNames[date.getDay()];

    // Check if resource has custom working profile enabled
    if (resource.useCustomWorkingProfile && resource.customWorkingProfile) {
      const hours = resource.customWorkingProfile[dayKey] || 0;
      return hours === 0;
    }

    // Otherwise use global working profile
    const hours = settings.globalWorkingProfile?.[dayKey] || 0;
    return hours === 0;
  }, [settings.globalWorkingProfile]);

  // Helper function to check if a specific time falls within non-business hours (Day view only)
  const isNonBusinessHour = useCallback((date: Date): boolean => {
    if (calendarView !== 'day') return false;

    const bizStartRaw = settings.businessHoursStart ?? 9;
    const bizEndRaw = settings.businessHoursEnd ?? 17;

    // Convert date's time to a decimal hour for comparison (e.g. 10:30 → 10.5)
    const timeDecimal = date.getHours() + date.getMinutes() / 60;

    return timeDecimal < bizStartRaw || timeDecimal >= bizEndRaw;
  }, [calendarView, settings.businessHoursStart, settings.businessHoursEnd]);

  const toggleAllGroups = useCallback(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) {
      return;
    }

    const shouldExpand = !allGroupsExpanded;

    // Access the tree feature directly
    const treeFeature = (scheduler as unknown as { features?: { tree?: { collapseAll?: () => void; expandAll?: () => void } } }).features?.tree;

    if (treeFeature) {
      if (shouldExpand && treeFeature.expandAll) {
        treeFeature.expandAll();
      } else if (!shouldExpand && treeFeature.collapseAll) {
        treeFeature.collapseAll();
      }
    }

    // Update button icon directly via DOM — minus-square when expanded, plus-square when collapsed
    const button = document.getElementById('expand-collapse-all-btn');
    if (button) {
      const newIcon = shouldExpand
        ? `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`
        : `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;
      button.innerHTML = newIcon;
      button.title = shouldExpand ? 'Collapse all' : 'Expand all';
    }

    setAllGroupsExpanded(shouldExpand);
  }, [allGroupsExpanded]);


  const cleanupPhantomEvents = useCallback(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    const eventStore = scheduler.eventStore;
    const bookingIds = new Set(bookings.map((b: Booking) => String(b.id)));
    const toRemove = (eventStore as any).allRecords?.filter(
      (rec: { id?: string; isPhantom?: boolean; data?: any; originalData?: any }) => {
        if (rec.isPhantom) return true;
        const eventId = String(rec.id || '');
        // Never remove heatmap events — they are keyed by tick index, not booking IDs,
        // and are managed entirely by the heatmapEvents useMemo + the store-sync effect.
        if ((rec as any).data?.isHeatmapEvent || (rec as any).isHeatmapEvent || eventId.startsWith('heatmap-')) return false;
        // Never remove what-if ghost events — they are managed by the whatIfScenario useEffect
        if (eventId.startsWith('__whatif_ghost__') || (rec as any).data?.isWhatIfGhost) return false;
        const hasOriginal = !!(rec as any).data?.originalBooking || !!(rec as any).originalData?.originalBooking;
        // Remove any event that isn't mapped from our data (no originalBooking)
        // OR whose id doesn't exist in our current bookings set
        return !hasOriginal || (eventId && !bookingIds.has(eventId));
      }
    ) || [];
    if (toRemove.length > 0) {
      eventStore.remove(toRemove);
    }
  }, [bookings]);


  useImperativeHandle(ref, () => ({
    revertEvent: (bookingId: string, originalResourceId: string, originalStartTime: Date, originalEndTime: Date) => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler) return;

      const eventStore = scheduler.eventStore;
      const eventRecord = eventStore.getById(bookingId);

      if (eventRecord) {
        console.log('🔄 Reverting event to original position:', {
          bookingId,
          originalResourceId,
          originalStartTime,
          originalEndTime,
          currentResourceId: (eventRecord as any).resourceId,
        });

        // Use batch update to ensure all changes are applied atomically
        eventRecord.beginBatch();

        // Update the dates
        (eventRecord as any).startDate = originalStartTime;
        (eventRecord as any).endDate = originalEndTime;

        // Update the resource ID directly - this works in single-assignment mode
        (eventRecord as any).resourceId = originalResourceId;

        eventRecord.endBatch();

        console.log('🔄 Event reverted, new resourceId:', (eventRecord as any).resourceId);
      }
    },
    cleanupPhantomEvents,
  }), [cleanupPhantomEvents]);

  // ── What-if ghost tile ────────────────────────────────────────────────────
  // Ghost tile injection is handled directly inside the main store-sync effect
  // below so that it is always atomic with the removeAll/add cycle and fires
  // exactly when the Bryntum scheduler is confirmed to be ready.
  // These constants are used by both the sync effect and cleanupPhantomEvents.
  const GHOST_EVENT_ID_PREFIX = '__whatif_ghost__';
  const MOVING_CLS = 'whatif-moving';

  // Helper: extract first-letter-of-first-name + first-letter-of-last-name
  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return '?';
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
    return (first + last).toUpperCase();
  };

  // Helper function to generate avatar URL - returns photoUrl or empty string
  // so the renderer can decide whether to show an <img> or the teal-gradient initials fallback
  const getAvatarUrl = (name: string, photoUrl?: string): string => {
    if (photoUrl && photoUrl.trim() !== '' && photoUrl !== 'null' && photoUrl !== 'undefined') return photoUrl;
    return '';
  };

  // Create tree structure with groups as parent nodes and resources as children
  // Uses the resources prop which is already filtered by ResourceCalendar
  const bryntumResources = useMemo(() => {
    // Helper function to check if a resource/project/booking matches the search query
    const matchesSearch = (searchFields: string[]): boolean => {
      if (!searchQuery.trim()) return true; // No search query, show all

      const query = searchQuery.toLowerCase();
      return searchFields.some(field =>
        field && field.toLowerCase().includes(query)
      );
    };

    // Compute max overlapping bookings per resource to dynamically size rows
    const BASE_ROW_HEIGHT = 50;
    const EXTRA_HEIGHT_PER_BOOKING = 25;
    const MAX_BOOKINGS_BEFORE_GROW = 2;

    const maxOverlapByResource = new Map<string | number, number>();
    const resourceBookingsMap = new Map<string | number, Array<{ start: number; end: number }>>();
    bookings.forEach(b => {
      const rid = b.resourceId;
      if (!rid) return;
      if (!resourceBookingsMap.has(rid)) resourceBookingsMap.set(rid, []);
      resourceBookingsMap.get(rid)!.push({
        start: new Date(b.startTime).getTime(),
        end: new Date(b.endTime).getTime(),
      });
    });
    resourceBookingsMap.forEach((intervals, rid) => {
      const points: Array<{ time: number; type: number }> = [];
      intervals.forEach(iv => {
        points.push({ time: iv.start, type: 1 });
        points.push({ time: iv.end, type: -1 });
      });
      points.sort((a, b) => a.time - b.time || a.type - b.type);
      let concurrent = 0;
      let maxConcurrent = 0;
      points.forEach(p => {
        concurrent += p.type;
        if (concurrent > maxConcurrent) maxConcurrent = concurrent;
      });
      maxOverlapByResource.set(rid, maxConcurrent);
    });

    const getDynamicRowHeight = (resourceId: string | number): number | undefined => {
      // In heatmap mode each row has exactly one event bar per tick — no stacking
      // occurs, so the row should always use the base height.
      if (isHeatmapMode) return undefined;
      const maxOverlap = maxOverlapByResource.get(resourceId) ?? 0;
      return maxOverlap > MAX_BOOKINGS_BEFORE_GROW
        ? BASE_ROW_HEIGHT + (maxOverlap - MAX_BOOKINGS_BEFORE_GROW) * EXTRA_HEIGHT_PER_BOOKING
        : undefined;
    };

    // Project & Resource grouping mode
    if (groupingMode === 'project-resource') {
      console.log('🔝 BryntumSchedulerTimeline - Project & Resource Mode');
      console.log('📊 Total bookings:', bookings.length);
      console.log('📊 Total projectDetails:', projectDetails.length);

      // Build map of projects to resources that have bookings for those projects
      const projectResourceMap = new Map<string, Set<string>>();

      bookings.forEach(booking => {
        console.log('📋 Checking booking:', {
          id: booking.id,
          name: (booking as any).name,
          projectId: booking.projectId,
          projectName: booking.projectName,
          resourceId: booking.resourceId,
          hasProjectId: !!booking.projectId && booking.projectId !== ''
        });

        if (booking.projectId && booking.projectId !== '') {
          console.log('✅ Booking has valid projectId:', booking.projectId);
          if (!projectResourceMap.has(booking.projectId)) {
            projectResourceMap.set(booking.projectId, new Set());
          }
          projectResourceMap.get(booking.projectId)!.add(booking.resourceId ?? "");
        } else {
          console.log('❌ Booking has NO projectId or empty projectId - will be skipped');
        }
      });

      console.log('📊 Project-Resource Map:', Array.from(projectResourceMap.entries()).map(([projectId, resourceIds]) => ({
        projectId,
        resourceCount: resourceIds.size,
        resourceIds: Array.from(resourceIds)
      })));

      // Create hierarchical resource structure: Projects > Resources
      const hierarchicalResources: Array<{
        id: string;
        name: string;
        expanded: boolean;
        icon: boolean;
        iconCls: string;
        isProjectGroup: boolean;
        projectColor?: string;
        projectCode?: string;
        children: Array<{
          id: string;
          originalResourceId: string;
          projectId: string;
          name: string;
          role?: string;
          department: string;
          schedulerGroup: string;
          imageUrl: string;
          icon: boolean;
          iconCls: string;
        }>;
      }> = [];

      projectDetails.forEach(project => {
        const resourcesForProject = projectResourceMap.get(String(project.id));

        // Only include projects that have bookings with resources
        if (resourcesForProject && resourcesForProject.size > 0) {
          const projectNode = {
            id: `project-${project.id}`,
            name: project.name,
            expanded: true,
            icon: false,
            iconCls: '',
            isProjectGroup: true,
            projectColor: project.color,
            projectCode: project.billingCode,
            children: [] as Array<{
              id: string;
              originalResourceId: string;
              projectId: string;
              name: string;
              role?: string;
              department: string;
              schedulerGroup: string;
              imageUrl: string;
              icon: boolean;
              iconCls: string;
            }>,
          };

          // Add resources as children under this project
          resourcesForProject.forEach(resourceId => {
            const resource = resources.find(r => String(r.id) === String(resourceId));
            if (resource) {
              // Get bookings for this resource and project to search in their fields
              const resourceProjectBookings = bookings.filter(
                b => String(b.resourceId) === String(resource.id) && String(b.projectId) === String(project.id)
              );

              // Collect all searchable fields for this resource-project combination
              const searchFields = [
                resource.name,
                resource.schedulerGroup || '',
                resource.department || '',
                project.billingCode || '',
                project.name,
                ...resourceProjectBookings.flatMap(booking => {
                  const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                  return [
                    costCentre?.code || '',
                    costCentre?.name || '',
                    booking.description || ''
                  ];
                })
              ];

              // Only add resource if it matches the search query
              if (matchesSearch(searchFields)) {
                const dynamicHeight = getDynamicRowHeight(resource.id);
                projectNode.children.push({
                  id: `${project.id}-${resource.id}`, // Unique ID for this project-resource combination
                  originalResourceId: resource.id, // Store original resource ID for booking operations
                  projectId: String(project.id), // Store project ID for filtering
                  name: resource.name,
                  role: resource.role,
                  department: resource.department || 'Unassigned',
                  schedulerGroup: resource.schedulerGroup || 'Unassigned',
                  imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                  icon: false,
                  iconCls: '',
                  ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
                });
              }
            }
          });

          // Sort children (resources) alphabetically by name in ascending order
          projectNode.children.sort((a, b) => (a.name || '').localeCompare((b as any).name || ''));

          // Only add project if it has children after filtering OR if the project itself matches the search
          const projectSearchFields = [project.name, project.billingCode || ''];
          if (projectNode.children.length > 0 || matchesSearch(projectSearchFields)) {
            hierarchicalResources.push(projectNode);
          }
        }
      });

      // Sort projects alphabetically by name in ascending order
      hierarchicalResources.sort((a, b) => (a.name || '').localeCompare((b as any).name || ''));

      console.log('✅ Final hierarchical resources:', hierarchicalResources.length, 'projects');
      console.log('📊 Projects with resources:', hierarchicalResources.map(p => ({
        projectId: p.id,
        projectName: p.name,
        resourceCount: p.children.length,
        resources: p.children.map(r => r.name)
      })));

      return hierarchicalResources;
    }

    // Role & Resource grouping mode - groups resources by their role
    if (groupingMode === 'role-resource') {
      console.log('🔝 BryntumSchedulerTimeline - Role & Resource Mode');
      console.log('📊 Total resources:', resources.length);

      // Group resources by role
      const roleResourceMap = new Map<string, Resource[]>();

      resources.forEach(resource => {
        // Special handling for Unallocated resource - use "System" as role
        const roleName = String(resource.id) === 'unallocated' ? 'System' : (resource.role || 'Unassigned');
        if (!roleResourceMap.has(roleName)) {
          roleResourceMap.set(roleName, []);
        }
        roleResourceMap.get(roleName)!.push(resource);
      });

      console.log('📊 Role-Resource Map:', Array.from(roleResourceMap.entries()).map(([role, roleResources]) => ({
        role,
        resourceCount: roleResources.length,
        resourceNames: roleResources.map(r => r.name)
      })));

      // Create hierarchical resource structure: Roles > Resources
      const hierarchicalResources: Array<{
        id: string;
        name: string;
        expanded: boolean;
        icon: boolean;
        iconCls: string;
        isRoleGroup: boolean;
        children: Array<{
          id: string;
          originalResourceId: string;
          roleId: string;
          name: string;
          role?: string;
          department: string;
          schedulerGroup: string;
          imageUrl: string;
          icon: boolean;
          iconCls: string;
        }>;
      }> = [];

      // Sort roles alphabetically, but put "Unassigned" and "System" at the end
      const sortedRoles = Array.from(roleResourceMap.keys()).sort((a, b) => {
        if (a === 'System') return 1; // System at the very end
        if (b === 'System') return -1;
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      });

      sortedRoles.forEach(roleName => {
        const roleResources = roleResourceMap.get(roleName)!;

        const roleNode = {
          id: `role-${roleName}`,
          name: roleName,
          // Collapse the "System" role group by default
          expanded: roleName !== 'System',
          icon: false,
          iconCls: '',
          isRoleGroup: true,
          // Sort children (resources) alphabetically by name in ascending order
          children: roleResources
            .sort((a, b) => (a.name || '').localeCompare((b as any).name || ''))
            .filter(resource => {
              // Get bookings for this resource to search in their fields
              const resourceBookings = bookings.filter(b => String(b.resourceId) === String(resource.id));

              // Collect all searchable fields for this resource
              const searchFields = [
                resource.name,
                resource.schedulerGroup || '',
                resource.department || '',
                roleName,
                ...resourceBookings.flatMap(booking => {
                  const project = projectDetails.find(p => p.id.toString() === booking.projectId);
                  const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                  return [
                    project?.billingCode || '',
                    project?.name || '',
                    booking.projectName || '',
                    costCentre?.code || '',
                    costCentre?.name || '',
                    booking.description || ''
                  ];
                })
              ];

              return matchesSearch(searchFields);
            })
            .map(resource => {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              return {
                id: `${roleName}-${resource.id}`, // Unique ID for this role-resource combination
                originalResourceId: resource.id, // Store original resource ID for booking operations
                roleId: roleName, // Store role for reference
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              };
            }),
        };

        // Only add role if it has children after filtering OR if the role itself matches the search
        const roleSearchFields = [roleName];
        if (roleNode.children.length > 0 || matchesSearch(roleSearchFields)) {
          hierarchicalResources.push(roleNode);
        }
      });

      console.log('✅ Final hierarchical resources:', hierarchicalResources.length, 'roles');
      console.log('📊 Roles with resources:', hierarchicalResources.map(r => ({
        roleId: r.id,
        roleName: r.name,
        resourceCount: r.children.length,
        resources: r.children.map(res => res.name)
      })));

      return hierarchicalResources;
    }

    // Customer grouping mode - groups resources by the customer name on their bookings
    if (groupingMode === 'customer') {
      console.log('🔝 BryntumSchedulerTimeline - Customer Mode');
      console.log('📊 Total bookings:', bookings.length);

      // Build a map of customer name -> Set of resource IDs that have bookings for that customer
      const customerResourceMap = new Map<string, Set<string>>();

      bookings.forEach(booking => {
        // Skip unallocated resource
        if (booking.resourceId === 'unallocated') return;

        // Derive customer name: use booking.customer, or look up via projectDetails clientName
        let customerName: string = booking.customer?.trim() || '';
        if (!customerName && booking.projectId) {
          const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
          customerName = project?.clientName?.trim() || '';
        }
        if (!customerName) customerName = 'Unassigned';

        if (!customerResourceMap.has(customerName)) {
          customerResourceMap.set(customerName, new Set());
        }
        customerResourceMap.get(customerName)!.add(String(booking.resourceId));
      });

      // Sort customer names alphabetically, Unassigned at the end
      const sortedCustomers = Array.from(customerResourceMap.keys()).sort((a, b) => {
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      });

      const hierarchicalResources: Array<{
        id: string;
        name: string;
        expanded: boolean;
        icon: boolean;
        iconCls: string;
        isCustomerGroup: boolean;
        children: Array<{
          id: string;
          originalResourceId: string;
          customerId: string;
          name: string;
          role?: string;
          department: string;
          schedulerGroup: string;
          imageUrl: string;
          icon: boolean;
          iconCls: string;
        }>;
      }> = [];

      sortedCustomers.forEach(customerName => {
        const resourceIdsForCustomer = customerResourceMap.get(customerName)!;

        const customerNode = {
          id: `customer-${customerName}`,
          name: customerName,
          expanded: true,
          icon: false,
          iconCls: '',
          isCustomerGroup: true,
          children: [] as Array<{
            id: string;
            originalResourceId: string;
            customerId: string;
            name: string;
            role?: string;
            department: string;
            schedulerGroup: string;
            imageUrl: string;
            icon: boolean;
            iconCls: string;
          }>,
        };

        resourceIdsForCustomer.forEach(resourceId => {
          const resource = resources.find(r => String(r.id) === resourceId);
          if (resource) {
            // Collect searchable fields for this customer-resource combination
            const resourceCustomerBookings = bookings.filter(b => {
              if (String(b.resourceId) !== String(resource.id)) return false;
              let bCustomer: string = b.customer?.trim() || '';
              if (!bCustomer && b.projectId) {
                const project = projectDetails.find(p => String(p.id) === String(b.projectId));
                bCustomer = project?.clientName?.trim() || '';
              }
              if (!bCustomer) bCustomer = 'Unassigned';
              return bCustomer === customerName;
            });

            const searchFields = [
              resource.name,
              resource.schedulerGroup || '',
              resource.department || '',
              customerName,
              ...resourceCustomerBookings.flatMap(booking => {
                const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
                const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                return [
                  project?.billingCode || '',
                  project?.name || '',
                  booking.projectName || '',
                  costCentre?.code || '',
                  costCentre?.name || '',
                  booking.description || '',
                ];
              }),
            ];

            if (matchesSearch(searchFields)) {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              customerNode.children.push({
                id: `${customerName}-${resource.id}`,
                originalResourceId: resource.id,
                customerId: customerName,
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              });
            }
          }
        });

        // Sort children alphabetically by resource name
        customerNode.children.sort((a, b) => (a.name || '').localeCompare((b as any).name || ''));

        const customerSearchFields = [customerName];
        if (customerNode.children.length > 0 || matchesSearch(customerSearchFields)) {
          hierarchicalResources.push(customerNode);
        }
      });

      console.log('✅ Final hierarchical resources:', hierarchicalResources.length, 'customers');

      return hierarchicalResources;
    }

    // Default Resource grouping mode (by department or scheduler group)
    if (settings.groupingMethod === 'scheduler-group') {
      // Use DB scheduler groups: group resources by schedulerGroupId,
      // only show groups that have resources, put unassigned resources under "Unassigned"
      const groupIdToName = new Map<number, string>();
      schedulerGroups.forEach(g => groupIdToName.set(Number(g.id), g.name));

      const groupedByGroupId: Record<string, Resource[]> = {};

      resources.forEach((resource: Resource) => {
        if (resource.schedulerGroupId != null && groupIdToName.has(resource.schedulerGroupId)) {
          const groupName = groupIdToName.get(resource.schedulerGroupId)!;
          if (!groupedByGroupId[groupName]) {
            groupedByGroupId[groupName] = [];
          }
          groupedByGroupId[groupName].push(resource);
        } else {
          // Resource has no group or group not found — put under "Unassigned"
          if (!groupedByGroupId['Unassigned']) {
            groupedByGroupId['Unassigned'] = [];
          }
          groupedByGroupId['Unassigned'].push(resource);
        }
      });

      const treeData = Object.entries(groupedByGroupId)
        .sort(([a], [b]) => {
          if (a === 'System') return 1;
          if (b === 'System') return -1;
          if (a === 'Unassigned') return 1;
          if (b === 'Unassigned') return -1;
          return a.localeCompare(b);
        })
        .map(([groupName, groupResources]) => {
          const filteredGroupResources = groupResources
            .filter((resource: Resource) => {
              const resourceBookings = bookings.filter(b => String(b.resourceId) === String(resource.id));
              const searchFields = [
                resource.name,
                resource.schedulerGroup || '',
                resource.department || '',
                groupName,
                ...resourceBookings.flatMap(booking => {
                  const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
                  const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                  return [
                    project?.billingCode || '',
                    project?.name || '',
                    booking.projectName || '',
                    costCentre?.code || '',
                    costCentre?.name || '',
                    booking.description || ''
                  ];
                })
              ];
              return matchesSearch(searchFields);
            });

          return {
            id: `group-${groupName}`,
            name: groupName,
            expanded: groupName !== 'System',
            icon: false,
            iconCls: '',
            children: filteredGroupResources
              .sort((a, b) => (a.name || '').localeCompare((b as any).name || ''))
              .map((resource: Resource) => {
                const dynamicHeight = getDynamicRowHeight(resource.id);
                return {
                  id: resource.id,
                  name: resource.name,
                  role: resource.role,
                  department: resource.department || 'Unassigned',
                  schedulerGroup: resource.schedulerGroup || 'Unassigned',
                  imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                  icon: false,
                  iconCls: '',
                  ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
                };
              }),
          };
        })
        .filter(group => {
          const groupSearchFields = [group.name];
          return group.children.length > 0 || matchesSearch(groupSearchFields);
        });

      return treeData;
    }

    // Default: group by department
    const groupedResources: Record<string, Resource[]> = {};

    resources.forEach((resource: Resource) => {
      const groupName = resource.department || 'Unassigned';
      if (!groupedResources[groupName]) {
        groupedResources[groupName] = [];
      }
      groupedResources[groupName].push(resource);
    });

    // Create tree structure with sorting
    const treeData = Object.entries(groupedResources)
      // Sort groups alphabetically, but put "System" and "Unassigned" at the end
      .sort(([a], [b]) => {
        if (a === 'System') return 1;
        if (b === 'System') return -1;
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      })
      .map(([groupName, groupResources]) => {
        // Filter resources based on search query
        const filteredGroupResources = groupResources
          .filter((resource: Resource) => {
            // Get bookings for this resource to search in their fields
            const resourceBookings = bookings.filter(b => String(b.resourceId) === String(resource.id));

            // Collect all searchable fields for this resource
            const searchFields = [
              resource.name,
              resource.schedulerGroup || '',
              resource.department || '',
              groupName,
              ...resourceBookings.flatMap(booking => {
                const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
                const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                return [
                  project?.billingCode || '',
                  project?.name || '',
                  booking.projectName || '',
                  costCentre?.code || '',
                  costCentre?.name || '',
                  booking.description || ''
                ];
              })
            ];

            return matchesSearch(searchFields);
          });

        return {
          id: `group-${groupName}`,
          name: groupName,
          // Collapse the "System" group (containing Unallocated) by default
          expanded: groupName !== 'System',
          icon: false,
          iconCls: '',
          // Sort children (resources) alphabetically by name in ascending order
          children: filteredGroupResources
            .sort((a, b) => (a.name || '').localeCompare((b as any).name || ''))
            .map((resource: Resource) => {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              return {
                id: resource.id,
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              };
            }),
        };
      })
      // Filter out groups with no children after filtering
      .filter(group => {
        // Keep group if it has children OR if the group name itself matches the search
        const groupSearchFields = [group.name];
        return group.children.length > 0 || matchesSearch(groupSearchFields);
      });

    return treeData;
  }, [resources, bookings, projects, projectDetails, costCentres, schedulerGroups, settings.groupingMethod, groupingMode, searchQuery]);

  // Uses the bookings prop which is already filtered by ResourceCalendar
  const bryntumEvents: BryntumEvent[] = useMemo(() => {
    // In heatmap mode, return empty array to hide all bookings
    if (isHeatmapMode) {
      return [];
    }

    return bookings
      .filter((booking: Booking) => {
        // For the Unallocated resource, only show bookings with booking type "Project" (bt-project-001 or bt-1)
        if (booking.resourceId === 'unallocated') {
          // Check if the booking type is "Project"
          const isProjectBooking = booking.bookingTypeId === 'bt-project-001' ||
            booking.bookingTypeId === 'bt-1' ||
            String(booking.bookingTypeId) === '1';
          return isProjectBooking;
        }
        // For all other resources, show all bookings
        return true;
      })
      .map((booking: Booking) => {
        // Parse booking times - stored without Z suffix to prevent UTC interpretation
        // Use the times directly without any timezone conversion to avoid 5:30 AM offset for IST users
        const startDate = new Date(booking.startTime);
        const endDate = new Date(booking.endTime);

        // Shift based on difference between booking timezone and current header timezone
        const sourceTz = (booking as any).timezone || 'Europe/London';
        const targetTz = selectedTimezone || sourceTz;
        const offsetMin = enableTimezones ? getTimezoneOffset(sourceTz, targetTz) : 0;
        let displayStartDate = new Date(startDate.getTime() + offsetMin * 60000);
        let displayEndDate = new Date(endDate.getTime() + offsetMin * 60000);

        // Compute display times. For half-day AM/PM, clamp to business hours halves
        if (booking.durationType === 'half-day' || booking.durationType === 'half-day-am' || booking.durationType === 'half-day-pm') {
          const businessStart = settings.businessHoursStart ?? 9;
          const businessEnd = settings.businessHoursEnd ?? 17;
          const halfPoint = businessStart + Math.max(1, Math.floor((businessEnd - businessStart) / 2));
          const day = new Date(startDate);
          day.setHours(0, 0, 0, 0);
          const period = booking.durationType === 'half-day'
            ? (booking.halfDayPeriod === 'pm' ? 'pm' : 'am')
            : (booking.durationType === 'half-day-am' ? 'am' : 'pm');
          if (period === 'am') {
            displayStartDate = new Date(day);
            displayStartDate.setHours(businessStart, 0, 0, 0);
            displayEndDate = new Date(day);
            displayEndDate.setHours(halfPoint, 0, 0, 0);
          } else {
            displayStartDate = new Date(day);
            displayStartDate.setHours(halfPoint, 0, 0, 0);
            displayEndDate = new Date(day);
            displayEndDate.setHours(businessEnd, 0, 0, 0);
          }
        }

        // Map resourceId based on grouping mode
        // This ensures bookings appear under the correct hierarchical node
        let resourceId: string;
        if (groupingMode === 'project-resource' && booking.projectId) {
          // In project-resource mode, map to the combined project-resource ID
          resourceId = `${booking.projectId}-${booking.resourceId}`;
        } else if (groupingMode === 'role-resource') {
          // In role-resource mode, map to the combined role-resource ID
          // Find the resource to get its role
          const resource = resources.find(r => String(r.id) === String(booking.resourceId));
          const roleName = resource?.role || 'Unassigned';
          resourceId = `${roleName}-${booking.resourceId}`;
        } else if (groupingMode === 'customer') {
          // In customer mode, map to the combined customer-resource ID
          // Derive customer name from booking.customer or project clientName
          let customerName: string = booking.customer?.trim() || '';
          if (!customerName && booking.projectId) {
            const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
            customerName = project?.clientName?.trim() || '';
          }
          if (!customerName) customerName = 'Unassigned';
          resourceId = `${customerName}-${booking.resourceId}`;
        } else {
          resourceId = booking.resourceId ?? "";
        }

        // Build CSS class list for the event
        const clsList: string[] = [];
        if (booking.isFiltered) clsList.push('filtered-booking');
        if (booking.recurrencePending) clsList.push('pending-recurring-booking');
        if (booking.recurrenceGroupId) clsList.push('recurring-booking');

        return {
          id: booking.id,
          resourceId: resourceId,
          name: booking.projectName || booking.description || 'Booking',
          startDate: displayStartDate,
          endDate: displayEndDate,
          eventColor: getBookingColor(booking, settings.statusConfigs, settings.colorOverrides),
          cls: clsList.length > 0 ? clsList.join(' ') : undefined,
          originalBooking: booking,
        };
      });
  }, [bookings, groupingMode, resources, projectDetails, isHeatmapMode, selectedTimezone, enableTimezones, settings.businessHoursStart, settings.businessHoursEnd, settings.statusConfigs, settings.colorOverrides]);


  const { startDate, endDate } = useMemo<{ startDate: Date; endDate: Date }>(() => {
    let start: Date;
    let end: Date;

    // CRITICAL: Always create a fresh Date object to avoid mutation issues
    // This prevents race conditions when selectedDate changes rapidly (e.g., navigating then clicking Today)
    const validDate = new Date(safeSelectedDate.getTime());

    // In Day view, restrict the timeline to visible hours from settings
    const visibleHoursStart = settings.visibleHoursStart ?? 7;
    const visibleHoursEnd = settings.visibleHoursEnd ?? 19;

    switch (calendarView) {
      case 'day':
        start = new Date(validDate);
        start.setHours(visibleHoursStart, 0, 0, 0);
        end = new Date(validDate);
        end.setHours(visibleHoursEnd, 0, 0, 0);
        break;
      case 'week':
        // Create new Date objects to avoid mutation issues
        start = startOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
        end = endOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
        break;
      case 'month':
        // CRITICAL: Create fresh Date objects for date-fns functions
        start = startOfMonth(new Date(validDate.getTime()));
        end = endOfMonth(new Date(validDate.getTime()));
        break;
      case 'quarter':
        start = startOfQuarter(new Date(validDate.getTime()));
        end = endOfQuarter(new Date(validDate.getTime()));
        break;
      case 'year':
        start = startOfYear(new Date(validDate.getTime()));
        end = endOfYear(new Date(validDate.getTime()));
        break;
      default:
        // Create new Date objects to avoid mutation issues
        start = startOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
        end = endOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
    }

    // CRITICAL VALIDATION: Ensure dates are valid before returning
    const isStartValid = start instanceof Date && !isNaN(start.getTime());
    const isEndValid = end instanceof Date && !isNaN(end.getTime());

    // If dates are inverted, swap them (this can happen during rapid state changes)
    if (isStartValid && isEndValid && start.getTime() >= end.getTime()) {
      console.warn('⚠︝ WARNING: Dates are inverted, swapping them:', {
        originalStart: start.toString(),
        originalEnd: end.toString(),
        calendarView,
        selectedDate: validDate.toString()
      });
      const temp = start;
      start = end;
      end = temp;
      console.log('✅ Dates swapped:', {
        newStart: start.toString(),
        newEnd: end.toString()
      });
    }

    const isOrderCorrect = isStartValid && isEndValid && start.getTime() < end.getTime();

    if (!isOrderCorrect) {
      console.error('🚨 CRITICAL: Invalid date range calculated in useMemo:', {
        start: start?.toString(),
        end: end?.toString(),
        selectedDate: validDate.toString(),
        calendarView,
        startTime: start?.getTime(),
        endTime: end?.getTime(),
        isStartValid,
        isEndValid,
        isOrderCorrect,
        reason: !isStartValid ? 'Invalid start' : !isEndValid ? 'Invalid end' : 'Start >= End'
      });
      // Fallback to a valid week range using current date
      const now = new Date();
      start = startOfWeek(now, { weekStartsOn: 1 });
      end = endOfWeek(now, { weekStartsOn: 1 });
      console.log('✅ Fallback dates applied in useMemo:', {
        start: start.toString(),
        end: end.toString(),
        startTime: start.getTime(),
        endTime: end.getTime()
      });
    }

    // Final safety check before returning - swap if still inverted
    if (start.getTime() >= end.getTime()) {
      console.error('🚨 CRITICAL: Dates still inverted after fallback! Swapping as last resort.');
      const temp = start;
      start = end;
      end = temp;
    }

    return { startDate: start, endDate: end };
  }, [safeSelectedDate, calendarView, settings.visibleHoursStart, settings.visibleHoursEnd]);

  // Set initialDatesRef ONCE with the first valid computed date range so that
  // the Bryntum scheduler mounts with correct dates. We intentionally do NOT
  // update this ref on subsequent renders — all date changes after mount are
  // handled imperatively via setTimeSpan() in effects. This prevents the
  // BryntumScheduler React wrapper from detecting date prop changes and trying
  // to apply startDate/endDate individually, which causes the "Invalid
  // start/end dates" error during large backward date jumps (e.g. clicking
  // "Today" after navigating 2-3 weeks into the future, where the new endDate
  // is before the old startDate).
  if (!hasSetInitialDates.current && startDate < endDate) {
    initialDatesRef.current = { startDate, endDate };
    hasSetInitialDates.current = true;
  }

  const viewPreset = useMemo(() => {
    // an extra class is added so CSS can target the parent cell for full-cell highlighting.
    switch (calendarView) {
      case 'day':
        // Day View zoom levels:
        // Min (0): Default hourly view
        // Max (1): 15 minute view with 15-min intervals in header
        if (zoomLevel >= 1) {
          return {
            tickWidth: 30,
            shiftIncrement: 1,
            shiftUnit: 'day',
            timeResolution: {
              unit: 'minute',
              increment: 15,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'dddd, MMMM Do YYYY',
              },
              {
                unit: 'hour',
                dateFormat: 'H',
              },
              {
                unit: 'minute',
                increment: 15,
                dateFormat: 'mm',
              },
            ],
          };
        }
        return {
          base: 'hourAndDay',
          timeResolution: {
            unit: 'hour',
            increment: 1,
          },
        };
      case 'week':
        // Week View zoom levels:
        // Min (0): Daily view (current)
        // Low (1): 12 hours view
        // Med (2): 6 hours view
        // High (3): 3 hours view
        // Max (4): 1 hour view
        if (zoomLevel === 4) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 1,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 1,
                dateFormat: 'HH',
              },
            ],
          };
        } else if (zoomLevel === 3) {
          return {
            tickWidth: 50,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 3,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 3,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 2) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 6,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 6,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 1) {
          return {
            tickWidth: 80,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 12,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 12,
                dateFormat: 'HH:mm',
              },
            ],
          };
        }
        // Default week view (zoomLevel 0) - show exactly 7 days (Monday to Sunday)
        return {
          tickWidth: 100,
          shiftIncrement: 1,
          shiftUnit: 'week',
          timeResolution: {
            unit: 'day',
            increment: 1,
          },
          headers: [
            {
              unit: 'week',
              dateFormat: 'YYYY MMMM DD',
            },
            {
              unit: 'day',
              dateFormat: 'DD ddd',
            },
          ],
        };
      case 'month':
        // Month View zoom levels (same as Week view):
        // Min (0): Daily view (default)
        // Low (1): 12 hours view
        // Med (2): 6 hours view
        // High (3): 3 hours view
        // Max (4): 1 hour view
        if (zoomLevel === 4) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 1,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 1,
                dateFormat: 'HH',
              },
            ],
          };
        } else if (zoomLevel === 3) {
          return {
            tickWidth: 50,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 3,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 3,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 2) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 6,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 6,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 1) {
          return {
            tickWidth: 80,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 12,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 12,
                dateFormat: 'HH:mm',
              },
            ],
          };
        }
        // Default month view (zoomLevel 0) - show exactly the days from start to end of the current month
        return {
          tickWidth: 50,
          shiftIncrement: 1,
          shiftUnit: 'month',
          timeResolution: {
            unit: 'day',
            increment: 1,
          },
          headers: [
            {
              unit: 'month',
              dateFormat: 'YYYY MMMM',
            },
            {
              unit: 'day',
              dateFormat: 'DD',
            },
          ],
        };
      case 'quarter':
        // Quarter view - show months as top header and weeks (W1, W2, etc.) as second header
        return {
          tickWidth: 80,
          shiftIncrement: 3,
          shiftUnit: 'month',
          timeResolution: {
            unit: 'week',
            increment: 1,
          },
          headers: [
            {
              unit: 'month',
              dateFormat: 'MMMM',
            },
            {
              unit: 'week',
              renderer: (startDate: Date) => {
                // Calculate week number of the year
                const start = new Date(startDate.getFullYear(), 0, 1);
                const diff = startDate.getTime() - start.getTime();
                const oneWeek = 1000 * 60 * 60 * 24 * 7;
                const weekNum = Math.ceil((diff / oneWeek) + 1);
                return `W${weekNum}`;
              },
            },
          ],
        };
      case 'year':
        // Year view - show months (Jan, Feb, Mar, etc.) as columns
        return {
          tickWidth: 100,
          shiftIncrement: 1,
          shiftUnit: 'year',
          timeResolution: {
            unit: 'month',
            increment: 1,
          },
          headers: [
            {
              unit: 'month',
              dateFormat: 'MMM',
            },
          ],
        };
      default:
        return {
          base: 'weekAndDay',
          tickWidth: 100,
          shiftIncrement: 1,
          shiftUnit: 'week',
          timeResolution: {
            unit: 'day',
            increment: 1,
          },
        };
    }
  }, [calendarView, zoomLevel]);

  // Capture the viewPreset on first render so the <BryntumScheduler> component
  // always receives a stable initial value.  All subsequent preset changes are
  // applied imperatively inside the store-sync useEffect (within suspendRefresh)
  // to prevent Bryntum's React wrapper from calling setViewPreset unguarded and
  // wiping the event store.
  if (!initialViewPresetRef.current) {
    initialViewPresetRef.current = viewPreset;
  }

  const heatmapEvents: BryntumEvent[] = useMemo(() => {
    if (!isHeatmapMode) return [];

    const events: BryntumEvent[] = [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

    const getWorkingHoursForDay = (resource: Resource, date: Date): number => {
      const dayKey = dayNames[date.getDay()];
      if (resource.useCustomWorkingProfile && resource.customWorkingProfile) {
        return (resource.customWorkingProfile as unknown as Record<string, number>)[dayKey] || 0;
      }
      return (settings.globalWorkingProfile as unknown as Record<string, number> | undefined)?.[dayKey] || 0;
    };

    const ticks: Array<{ start: Date; end: Date }> = [];
    let tickCurrent = new Date(startDate);

    while (tickCurrent < endDate) {
      const next = new Date(tickCurrent);
      switch (calendarView) {
        case 'day':
          next.setHours(next.getHours() + 1);
          break;
        case 'week':
        case 'month':
          next.setDate(next.getDate() + 1);
          break;
        case 'quarter':
          next.setDate(next.getDate() + 7);
          break;
        case 'year':
          next.setMonth(next.getMonth() + 1);
          break;
        default:
          next.setDate(next.getDate() + 1);
      }
      ticks.push({
        start: new Date(tickCurrent),
        end: next > endDate ? new Date(endDate) : next,
      });
      tickCurrent = next;
    }

    const resourceMappings: Array<{ resource: Resource; bryntumId: string; projectFilter?: string; customerFilter?: string }> = [];

    if (groupingMode === 'project-resource') {
      resources.forEach((resource) => {
        const projectIds = new Set<string>();
        bookings.forEach((b) => {
          if (String(b.resourceId) === String(resource.id) && b.projectId) {
            projectIds.add(b.projectId);
          }
        });
        if (projectIds.size === 0) {
          resourceMappings.push({ resource, bryntumId: resource.id });
        } else {
          projectIds.forEach((projectId) => {
            resourceMappings.push({
              resource,
              bryntumId: `${projectId}-${resource.id}`,
              projectFilter: projectId,
            });
          });
        }
      });
    } else if (groupingMode === 'role-resource') {
      resources.forEach((resource) => {
        const roleName = resource.role || 'Unassigned';
        resourceMappings.push({ resource, bryntumId: `${roleName}-${resource.id}` });
      });
    } else if (groupingMode === 'customer') {
      // Build a map of customerName -> Set of resource IDs (same logic as the row builder)
      const customerResourceMap = new Map<string, Set<string>>();
      bookings.forEach((b) => {
        if (b.resourceId === 'unallocated') return;
        let customerName: string = b.customer?.trim() || '';
        if (!customerName && b.projectId) {
          const project = projectDetails.find(p => String(p.id) === String(b.projectId));
          customerName = project?.clientName?.trim() || '';
        }
        if (!customerName) customerName = 'Unassigned';
        if (!customerResourceMap.has(customerName)) {
          customerResourceMap.set(customerName, new Set());
        }
        customerResourceMap.get(customerName)!.add(String(b.resourceId));
      });

      customerResourceMap.forEach((resourceIds, customerName) => {
        resourceIds.forEach((resourceId) => {
          const resource = resources.find(r => String(r.id) === resourceId);
          if (resource) {
            resourceMappings.push({
              resource,
              bryntumId: `${customerName}-${resource.id}`,
              customerFilter: customerName,
            });
          }
        });
      });
    } else {
      resources.forEach((resource) => {
        resourceMappings.push({ resource, bryntumId: resource.id });
      });
    }

    resourceMappings.forEach(({ resource, bryntumId, projectFilter, customerFilter }) => {
      const resourceBookings = bookings.filter((b) => {
        if (String(b.resourceId) !== String(resource.id)) return false;
        if (projectFilter && String(b.projectId) !== String(projectFilter)) return false;
        if (customerFilter) {
          // Derive booking customer name the same way as the row builder
          let bCustomer: string = b.customer?.trim() || '';
          if (!bCustomer && b.projectId) {
            const project = projectDetails.find(p => String(p.id) === String(b.projectId));
            bCustomer = project?.clientName?.trim() || '';
          }
          if (!bCustomer) bCustomer = 'Unassigned';
          if (bCustomer !== customerFilter) return false;
        }
        return true;
      });

      ticks.forEach((tick, tickIndex) => {
        let availableHours = 0;
        const tickDurationMs = tick.end.getTime() - tick.start.getTime();
        const tickDurationHours = tickDurationMs / (1000 * 60 * 60);

        if (tickDurationHours >= 24) {
          const d = new Date(tick.start);
          while (d < tick.end) {
            availableHours += getWorkingHoursForDay(resource, d);
            d.setDate(d.getDate() + 1);
          }
        } else {
          const dayHours = getWorkingHoursForDay(resource, tick.start);
          if (dayHours > 0) {
            const businessStart = settings.businessHoursStart || 9;
            const businessEnd = businessStart + dayHours;
            const tickStartHour = tick.start.getHours() + tick.start.getMinutes() / 60;
            const tickEndHour = tickStartHour + tickDurationHours;
            const overlapStart = Math.max(tickStartHour, businessStart);
            const overlapEnd = Math.min(tickEndHour, businessEnd);
            availableHours = Math.max(0, overlapEnd - overlapStart);
          }
        }

        let bookedHours = 0;
        const matchedBookings: Booking[] = [];
        resourceBookings.forEach((booking) => {
          const bookingStart = new Date(booking.startTime);
          const bookingEnd = new Date(booking.endTime);
          if (bookingStart < tick.end && bookingEnd > tick.start) {
            matchedBookings.push(booking);
            // Cancelled bookings are excluded from hour calculations — they
            // represent work that was removed and should not affect utilisation
            // or the planned-only detection logic.
            if (booking.status === 'cancelled') return;
            if (booking.durationType === 'full-day') {
              if (tickDurationHours >= 24) {
                // Normalise bookingStart/End to midnight so day-boundary comparisons
                // work correctly regardless of the time-of-day stored in startTime/endTime.
                const bookingStartDay = new Date(bookingStart); bookingStartDay.setHours(0, 0, 0, 0);
                const bookingEndDay   = new Date(bookingEnd);   bookingEndDay.setHours(0, 0, 0, 0);
                const d = new Date(tick.start);
                while (d < tick.end) {
                  if (bookingStartDay <= d && bookingEndDay > d) {
                    bookedHours += getWorkingHoursForDay(resource, d);
                  }
                  d.setDate(d.getDate() + 1);
                }
              } else {
                bookedHours += getWorkingHoursForDay(resource, tick.start);
              }
            } else if (booking.durationType === 'half-day' || booking.durationType === 'half-day-am' || booking.durationType === 'half-day-pm') {
              if (tickDurationHours >= 24) {
                // Same normalisation for half-day bookings.
                const bookingStartDay = new Date(bookingStart); bookingStartDay.setHours(0, 0, 0, 0);
                const bookingEndDay   = new Date(bookingEnd);   bookingEndDay.setHours(0, 0, 0, 0);
                const d = new Date(tick.start);
                while (d < tick.end) {
                  if (bookingStartDay <= d && bookingEndDay > d) {
                    bookedHours += getWorkingHoursForDay(resource, d) / 2;
                  }
                  d.setDate(d.getDate() + 1);
                }
              } else {
                bookedHours += getWorkingHoursForDay(resource, tick.start) / 2;
              }
            } else {
              const overlapStart = bookingStart > tick.start ? bookingStart : tick.start;
              const overlapEnd = bookingEnd < tick.end ? bookingEnd : tick.end;
              bookedHours += (overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60);
            }
          }
        });

        // Sort matched bookings chronologically by start time
        matchedBookings.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

        // ── Confirmed / Planned hours split ──────────────────────────────────
        let confirmedHours = 0;
        let plannedHours = 0;
        matchedBookings.forEach((b) => {
          const bStart = new Date(b.startTime);
          const bEnd = new Date(b.endTime);
          let bHours = 0;
          if (b.durationType === 'full-day') {
            // Normalise to midnight for day-boundary comparisons.
            const bStartDay = new Date(bStart); bStartDay.setHours(0, 0, 0, 0);
            const bEndDay   = new Date(bEnd);   bEndDay.setHours(0, 0, 0, 0);
            const d = new Date(tick.start);
            while (d < tick.end) {
              if (bStartDay <= d && bEndDay > d) bHours += getWorkingHoursForDay(resource, d);
              d.setDate(d.getDate() + 1);
            }
          } else if (b.durationType === 'half-day' || b.durationType === 'half-day-am' || b.durationType === 'half-day-pm') {
            // Mirror the bookedHours loop: iterate days so multi-day half-day
            // bookings accumulate correctly rather than only counting tick.start.
            const bStartDay = new Date(bStart); bStartDay.setHours(0, 0, 0, 0);
            const bEndDay   = new Date(bEnd);   bEndDay.setHours(0, 0, 0, 0);
            const d = new Date(tick.start);
            while (d < tick.end) {
              if (bStartDay <= d && bEndDay > d) bHours += getWorkingHoursForDay(resource, d) / 2;
              d.setDate(d.getDate() + 1);
            }
          } else {
            const oStart = bStart > tick.start ? bStart : tick.start;
            const oEnd = bEnd < tick.end ? bEnd : tick.end;
            bHours = Math.max(0, (oEnd.getTime() - oStart.getTime()) / (1000 * 60 * 60));
          }
          if (b.status === 'confirmed' || b.status === 'completed') {
            confirmedHours += bHours;
          } else if (b.status !== 'cancelled') {
            // Only count as planned if genuinely pending/tentative — cancelled
            // bookings have already been excluded from bHours above but we guard
            // here explicitly so that custom status keys don't accidentally leak in.
            plannedHours += bHours;
          }
        });

        // ── Absence hours for this resource in this tick ─────────────────────
        let absenceHours = 0;
        const absenceReasonSet = new Set<string>();
        const businessHoursStart = settings.businessHoursStart || 9;
        const businessHoursEnd = settings.businessHoursEnd || 17;
        absences
          .filter((abs) => String(abs.resourceId) === String(resource.id))
          .forEach((abs) => {
            const absStart = new Date(abs.startDate);
            absStart.setHours(0, 0, 0, 0);
            const absEnd = new Date(abs.endDate);
            absEnd.setHours(23, 59, 59, 999);
            if (absStart >= tick.end || absEnd <= tick.start) return;
            // compute overlap hours
            let aHours = 0;
            if (abs.type === 'full-day') {
              const d = new Date(tick.start);
              while (d < tick.end) {
                const dEnd = new Date(d); dEnd.setHours(23, 59, 59, 999);
                if (absStart <= d && absEnd >= dEnd) aHours += getWorkingHoursForDay(resource, d);
                d.setDate(d.getDate() + 1);
              }
            } else if (abs.type === 'half-day-am') {
              aHours = getWorkingHoursForDay(resource, tick.start) / 2;
            } else if (abs.type === 'half-day-pm') {
              aHours = getWorkingHoursForDay(resource, tick.start) / 2;
            } else if (abs.type === 'hourly' && abs.startTime && abs.endTime) {
              const [sh, sm] = abs.startTime.split(':').map(Number);
              const [eh, em] = abs.endTime.split(':').map(Number);
              const absHStart = new Date(tick.start); absHStart.setHours(sh, sm, 0, 0);
              const absHEnd = new Date(tick.start); absHEnd.setHours(eh, em, 0, 0);
              const oS = absHStart > tick.start ? absHStart : tick.start;
              const oE = absHEnd < tick.end ? absHEnd : tick.end;
              aHours = Math.max(0, (oE.getTime() - oS.getTime()) / (1000 * 60 * 60));
            } else {
              aHours = getWorkingHoursForDay(resource, tick.start);
            }
            if (aHours > 0) {
              absenceHours += aHours;
              absenceReasonSet.add(abs.reason || 'Absence');
            }
          });
        // Also surface bank holidays
        bankHolidays.forEach((bh) => {
          const bhDate = new Date(bh.date);
          bhDate.setHours(0, 0, 0, 0);
          const bhEnd = new Date(bhDate); bhEnd.setHours(23, 59, 59, 999);
          if (bhDate >= tick.start && bhEnd < tick.end) {
            const dayHrs = getWorkingHoursForDay(resource, bhDate);
            if (dayHrs > 0) {
              absenceHours += dayHrs;
              absenceReasonSet.add(bh.name || 'Bank Holiday');
            }
          }
        });

        // ── Hard conflict detection: any two bookings with genuinely overlapping times ─
        let hasHardConflict = false;
        const nonCancelledBookings = matchedBookings.filter(b => b.status !== 'cancelled');
        outer: for (let i = 0; i < nonCancelledBookings.length; i++) {
          for (let j = i + 1; j < nonCancelledBookings.length; j++) {
            const aS = new Date(nonCancelledBookings[i].startTime);
            const aE = new Date(nonCancelledBookings[i].endTime);
            const bS = new Date(nonCancelledBookings[j].startTime);
            const bE = new Date(nonCancelledBookings[j].endTime);
            if (aS < bE && aE > bS) { hasHardConflict = true; break outer; }
          }
        }

        const utilization = availableHours > 0 ? (bookedHours / availableHours) * 100 : 0;

        let color: string;
        let band: string;
        if (availableHours === 0) {
          color = '#f5f5f5'; band = 'Non-working';
        } else {
          // bookedHours === 0 (unbooked) is handled by the early-return below,
          // so here we always have actual utilisation to colour.
          color = bookedHours === 0
            ? 'transparent'
            : getUtilizationColor(utilization, settings.utilisationTiers);
          band = bookedHours === 0
            ? 'Unbooked'
            : getUtilizationLabel(utilization, settings.utilisationTiers);
        }

        // ── Planned-only / mixed detection ────────────────────────────────
        // isPlannedOnly: has bookings but NONE are confirmed/completed.
        // isMixed:       has BOTH confirmed and planned hours in the same cell.
        // plannedRatio:  share of total booked hours that are unconfirmed (0–1).
        const isPlannedOnly = matchedBookings.length > 0 && confirmedHours === 0 && plannedHours > 0;
        const isMixed       = confirmedHours > 0 && plannedHours > 0;
        const plannedRatio  = bookedHours > 0 ? plannedHours / bookedHours : 0;

        // Forecast views: Month, Quarter, Year
        const isForecastView = calendarView === 'month' || calendarView === 'quarter' || calendarView === 'year';

        // Per-view threshold: use forecastSettings if present, fall back to legacy single value
        const fs = settings.forecastSettings;
        const warnThreshold = fs
          ? (calendarView === 'month' ? fs.monthThreshold
            : calendarView === 'quarter' ? fs.quarterThreshold
            : calendarView === 'year' ? fs.yearThreshold
            : 70)
          : (settings.plannedOnlyWarningThreshold ?? 70);
        const showPlannedPrefix = fs ? fs.showPlannedPrefix : true;

        // ── CSS class composition ──────────────────────────────────────────
        // Priority order: conflict > mixed > confirmed
        // Note: planned-only dashed border has been removed; ◈ prefix is the
        // only remaining visual treatment for fully-planned cells.
        let cellCls = 'b-heatmap-event';
        if (hasHardConflict) {
          cellCls += ' b-heatmap-conflict';
        } else if (isMixed && isForecastView && availableHours > 0) {
          // Mix of confirmed + planned — show split bar, intensity based on planned ratio
          cellCls += plannedRatio > 0.5
            ? ' b-heatmap-mixed b-heatmap-mixed-majority'   // majority unconfirmed
            : ' b-heatmap-mixed';                           // minority unconfirmed
        }

        // ── Cell label ────────────────────────────────────────────────────
        // ◈  = fully planned-only (all unconfirmed) — prefix only, no dashed border
        // ◑  = mixed, majority planned (>50% unconfirmed)
        // ⚠  = hard scheduling conflict
        const cellLabel = (() => {
          if (availableHours === 0) return '';
          if (bookedHours === 0) return ''; // empty day — no label needed
          const pct = `${Math.round(utilization)}%`;
          if (hasHardConflict) return `⚠ ${pct}`;
          if (isPlannedOnly && isForecastView && showPlannedPrefix) return `◈ ${pct}`;
          if (isMixed && isForecastView && showPlannedPrefix && plannedRatio > 0.5) return `◑ ${pct}`;
          return pct;
        })();

        // Skip creating an event for non-working day ticks — they have no
        // available hours and should appear as empty (no overlay / label).
        if (availableHours === 0) return;

        // Skip creating an event for genuinely unbooked ticks — transparent
        // Bryntum event nodes still render visible borders/outlines even with
        // color = 'transparent', producing stray lines on otherwise empty rows.
        if (bookedHours === 0) return;

        events.push({
          id: `heatmap-${bryntumId}-${tickIndex}`,
          resourceId: bryntumId,
          name: cellLabel,
          startDate: tick.start,
          endDate: tick.end,
          eventColor: color,
          cls: cellCls,
          isHeatmapEvent: true,
          heatmapData: {
            bookedHours: Math.round(bookedHours * 10) / 10,
            availableHours: Math.round(availableHours * 10) / 10,
            utilization: Math.round(utilization),
            band,
            bandColor: color,
            bookings: matchedBookings,
            confirmedHours: Math.round(confirmedHours * 10) / 10,
            plannedHours: Math.round(plannedHours * 10) / 10,
            absenceHours: Math.round(absenceHours * 10) / 10,
            absenceReasons: Array.from(absenceReasonSet),
            hasHardConflict,
            weeklyAvgUtilization: null, // populated in second pass below
            isPlannedOnly,
            isMixed,
            plannedRatio: Math.round(plannedRatio * 100) / 100,
          },
        });
      });
    });

    // ── Second pass: compute weekly average utilization per resource per week ──
    // Key: `${resourceId}::${ISO-week-string}` e.g. "res-1::2026-W16"
    // This means every event gets the avg for *its own* calendar week, regardless
    // of what week the view is anchored to — fixing the bug where events outside
    // startOfWeek(viewStart) got weeklyAvgUtilization = null.
    const weekBuckets = new Map<string, { sum: number; count: number }>();

    events.forEach((ev) => {
      if (!ev.heatmapData || ev.heatmapData.availableHours === 0) return;
      // Derive ISO week key from the event's own start date
      const evWeekStart = startOfWeek(ev.startDate as Date, { weekStartsOn: 1 });
      const isoKey = format(evWeekStart, 'yyyy-MM-dd');           // e.g. "2026-04-14" (Monday)
      const bucketKey = `${ev.resourceId}::${isoKey}`;
      const bucket = weekBuckets.get(bucketKey) ?? { sum: 0, count: 0 };
      bucket.sum += ev.heatmapData.utilization;
      bucket.count += 1;
      weekBuckets.set(bucketKey, bucket);
    });

    // Stamp the correct week's average onto every event
    events.forEach((ev) => {
      if (!ev.heatmapData) return;
      const evWeekStart = startOfWeek(ev.startDate as Date, { weekStartsOn: 1 });
      const isoKey = format(evWeekStart, 'yyyy-MM-dd');
      const bucketKey = `${ev.resourceId}::${isoKey}`;
      const bucket = weekBuckets.get(bucketKey);
      ev.heatmapData.weeklyAvgUtilization = bucket
        ? Math.round(bucket.sum / bucket.count)
        : null;
    });

    return events;
  }, [isHeatmapMode, startDate, endDate, calendarView, resources, bookings, absences, bankHolidays, groupingMode, settings]);

  const findResourceById = useCallback((id: string): Resource | undefined => {
    // First try direct match
    let resource = resources.find((r: Resource) => String(r.id) === String(id));

    // In project-resource mode, the ID might be a combined "projectId-resourceId"
    // Try to extract the actual resource ID
    if (!resource && groupingMode === 'project-resource' && id.includes('-') && !id.startsWith('group-') && !id.startsWith('project-')) {
      const parts = id.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (project ID)
        const actualResourceId = parts.slice(1).join('-');
        resource = resources.find((r: Resource) => String(r.id) === String(actualResourceId));
      }
    }

    // In role-resource mode, the ID might be a combined "roleName-resourceId"
    // Try to extract the actual resource ID
    if (!resource && groupingMode === 'role-resource' && id.includes('-') && !id.startsWith('group-') && !id.startsWith('role-')) {
      const parts = id.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (role name)
        const actualResourceId = parts.slice(1).join('-');
        resource = resources.find((r: Resource) => String(r.id) === String(actualResourceId));
      }
    }

    // In customer mode, the ID might be a combined "customerName-resourceId"
    // Try to match the actual resource by finding one whose ID the composite ends with
    if (!resource && groupingMode === 'customer' && id.includes('-') && !id.startsWith('group-') && !id.startsWith('customer-')) {
      // Find any resource whose id appears at the end of the composite ID after a hyphen
      resource = resources.find((r: Resource) => {
        const rid = String(r.id);
        return id === `${id.slice(0, id.length - rid.length - 1)}-${rid}` && id.endsWith(`-${rid}`);
      });
      if (!resource) {
        // Fallback: take everything after the first hyphen
        const dashIdx = id.indexOf('-');
        if (dashIdx !== -1) {
          const actualResourceId = id.slice(dashIdx + 1);
          resource = resources.find((r: Resource) => String(r.id) === actualResourceId);
        }
      }
    }

    return resource;
  }, [resources, groupingMode]);

  const findBookingById = useCallback((id: string): Booking | undefined => {
    return bookings.find((b: Booking) => String(b.id) === String(id));
  }, [bookings]);

  const calculateResourceUtilization = useCallback((resourceId: string): number => {
    const resource = resources.find((r: Resource) => String(r.id) === String(resourceId));
    if (!resource) return 0;

    const isStartValid = startDate instanceof Date && !isNaN(startDate.getTime());
    const isEndValid = endDate instanceof Date && !isNaN(endDate.getTime());
    if (!isStartValid || !isEndValid || startDate.getTime() >= endDate.getTime()) {
      return 0;
    }

    const { utilizationPercentage } = calculatePeriodUtilization(
      String(resourceId),
      startDate,
      endDate,
      bookings,
      resource,
      settings.globalWorkingProfile
    );

    return utilizationPercentage;
  }, [resources, bookings, startDate, endDate, settings.globalWorkingProfile]);

  // Pre-calculate utilization for all resources to use in the column renderer
  const resourceUtilizationMap = useMemo(() => {
    const map = new Map<string, number>();
    resources.forEach((resource: Resource) => {
      const utilization = calculateResourceUtilization(String(resource.id));
      map.set(String(resource.id), utilization);

      if (groupingMode === 'project-resource') {
        const resourceProjects = new Set<string>();
        bookings.forEach((booking: Booking) => {
          if (String(booking.resourceId) === String(resource.id) && booking.projectId) {
            resourceProjects.add(String(booking.projectId));
          }
        });

        resourceProjects.forEach(projectId => {
          const combinedId = `${projectId}-${resource.id}`;
          map.set(combinedId, utilization);
        });
      }

      if (groupingMode === 'role-resource') {
        const roleName = String(resource.id) === 'unallocated' ? 'System' : (resource.role || 'Unassigned');
        const combinedId = `${roleName}-${resource.id}`;
        map.set(combinedId, utilization);
      }
    });
    return map;
  }, [resources, calculateResourceUtilization, groupingMode, bookings, startDate, endDate]);

  const resourceUtilizationMapRef = useRef(resourceUtilizationMap);
  resourceUtilizationMapRef.current = resourceUtilizationMap;

  // Get utilization color based on settings tiers
  const getResourceUtilizationColor = useCallback((utilization: number): string => {
    return getUtilizationColor(utilization, settings.utilisationTiers);
  }, [settings.utilisationTiers]);

  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler || scheduler.isDestroyed) return;
    const id1 = requestAnimationFrame(() => {
      if (!scheduler.isDestroyed) scheduler.refreshRows();
    });
    const id2 = setTimeout(() => {
      if (!scheduler.isDestroyed) scheduler.refreshRows();
    }, 150);
    return () => {
      cancelAnimationFrame(id1);
      clearTimeout(id2);
    };
  }, [resourceUtilizationMap, bookings, startDate, endDate]);

  // ─── Heatmap rich tooltip ────────────────────────────────────────────────────
  const generateHeatmapTooltipHtml = useCallback((hData: NonNullable<BryntumEvent['heatmapData']>, tickStart: Date): string => {
    // Don't show a tooltip at all when there are no bookings on this cell
    if (hData.bookings.length === 0) return '';

    // Determine if bandColor is dark so we pick a contrasting text colour
    const hexToRgb = (hex: string) => {
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return { r, g, b };
    };
    const rgb = hexToRgb(hData.bandColor || '#6b7280');
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    const headerTextColor = luminance > 0.55 ? '#1f2937' : '#ffffff';

    // Forecast view context for tooltip
    const isForecastViewTooltip = calendarView === 'month' || calendarView === 'quarter' || calendarView === 'year';
    const fsTooltip = settings.forecastSettings;
    const warnThresholdTooltip = fsTooltip
      ? (calendarView === 'month' ? fsTooltip.monthThreshold
        : calendarView === 'quarter' ? fsTooltip.quarterThreshold
        : calendarView === 'year' ? fsTooltip.yearThreshold
        : 70)
      : (settings.plannedOnlyWarningThreshold ?? 70);
    const showSplitTooltip = fsTooltip ? fsTooltip.showConfirmedPlannedSplit : true;

    // Format helpers
    const fmtTime = (iso: string) => {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    const fmtDuration = (startIso: string, endIso: string, durationType: string): string => {
      if (durationType === 'full-day') return 'Full day';
      if (durationType === 'half-day' || durationType === 'half-day-am') return 'Half day (AM)';
      if (durationType === 'half-day-pm') return 'Half day (PM)';
      const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
      const totalMins = Math.round(ms / 60000);
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      if (h === 0) return `${m}m`;
      if (m === 0) return `${h}h`;
      return `${h}h ${m}m`;
    };
    const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    const statusDot = (status: string): string => {
      const colors: Record<string, string> = {
        confirmed: '#10b981', planned: '#3b82f6', tentative: '#f59e0b',
        cancelled: '#ef4444', completed: '#6b7280',
      };
      const c = colors[status?.toLowerCase()] || '#9ca3af';
      return `<span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${c};margin-right:4px;flex-shrink:0;margin-top:2px;"></span>`;
    };

    const clockSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;
    const tagSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>`;
    const briefcaseSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>`;
    const pinSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>`;

    // ── Confirmed / Planned split label ──────────────────────────────────────
    const confirmedPlannedDetail = (() => {
      // Respect the showConfirmedPlannedSplit toggle on forecast views
      if (isForecastViewTooltip && !showSplitTooltip) return '';
      const parts: string[] = [];
      if (hData.confirmedHours > 0) {
        parts.push(`<span style="display:inline-flex;align-items:center;gap:3px;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:#10b981;flex-shrink:0;"></span><span>${hData.confirmedHours}h confirmed</span></span>`);
      }
      if (hData.plannedHours > 0) {
        parts.push(`<span style="display:inline-flex;align-items:center;gap:3px;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:#3b82f6;flex-shrink:0;"></span><span>${hData.plannedHours}h planned</span></span>`);
      }
      if (parts.length === 0) return '';
      return `<div style="font-size:11px;color:#6b7280;padding:5px 12px 2px;display:flex;gap:10px;flex-wrap:wrap;">${parts.join('')}</div>`;
    })();

    // ── Forecast banner (Month/Quarter/Year views) ────────────────────────────
    // Covers three states: fully planned-only, mixed-majority-planned, mixed-minority-planned
    const forecastBanner = (() => {
      if (!isForecastViewTooltip || hData.bookedHours === 0) return '';

      const { isPlannedOnly, isMixed, plannedRatio, utilization } = hData;
      if (!isPlannedOnly && !isMixed) return ''; // fully confirmed — no banner needed

      // ── Determine banner tier ──────────────────────────────────────────
      // Tier A: fully planned-only, high risk (≥ threshold)
      // Tier B: fully planned-only, low risk (< threshold)
      // Tier C: mixed, majority planned (>50%)
      // Tier D: mixed, minority planned (≤50%)
      type BannerTier = 'A' | 'B' | 'C' | 'D';
      const tier: BannerTier = isPlannedOnly
        ? (utilization >= warnThresholdTooltip ? 'A' : 'B')
        : (plannedRatio > 0.5 ? 'C' : 'D');

      const plannedPct = Math.round((plannedRatio ?? 0) * 100);
      const confirmedPct = 100 - plannedPct;

      const config: Record<BannerTier, { bg: string; border: string; text: string; iconColor: string; label: string; icon: 'warn' | 'info' }> = {
        A: {
          bg: '#fef3c7', border: '#fde68a', text: '#92400e', iconColor: '#d97706',
          label: `Forecast — all ${hData.bookedHours}h unconfirmed at ${utilization}% utilisation`,
          icon: 'warn',
        },
        B: {
          bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', iconColor: '#3b82f6',
          label: `Forecast — all bookings are planned (unconfirmed)`,
          icon: 'info',
        },
        C: {
          bg: '#fff7ed', border: '#fed7aa', text: '#9a3412', iconColor: '#ea580c',
          label: `Forecast — ${plannedPct}% planned, ${confirmedPct}% confirmed`,
          icon: 'warn',
        },
        D: {
          bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', iconColor: '#16a34a',
          label: `Mixed — ${confirmedPct}% confirmed, ${plannedPct}% planned`,
          icon: 'info',
        },
      };

      const { bg, border, text, iconColor, label, icon } = config[tier];
      const svgIcon = icon === 'warn'
        ? `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`
        : `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;

      // Split ratio bar — shows confirmed (green) / planned (blue) proportion visually
      const splitBar = isMixed
        ? `<div style="margin-top:5px;height:4px;border-radius:2px;overflow:hidden;background:#e5e7eb;display:flex;">
             <div style="width:${confirmedPct}%;background:#10b981;"></div>
             <div style="width:${plannedPct}%;background:#3b82f6;"></div>
           </div>
           <div style="display:flex;justify-content:space-between;margin-top:2px;">
             <span style="font-size:10px;color:#6b7280;">🟢 ${hData.confirmedHours}h confirmed</span>
             <span style="font-size:10px;color:#6b7280;">🔵 ${hData.plannedHours}h planned</span>
           </div>`
        : '';

      return `<div style="padding:6px 12px ${isMixed ? '8px' : '6px'};background:${bg};border-bottom:1px solid ${border};">
        <div style="display:flex;align-items:center;gap:6px;">
          ${svgIcon}
          <span style="font-size:11px;color:${text};font-weight:500;">${label}</span>
        </div>
        ${splitBar}
      </div>`;
    })();

    // ── Absence / leave row ───────────────────────────────────────────────────
    const absenceRow = hData.absenceHours > 0
      ? `<div style="display:flex;align-items:center;gap:6px;padding:5px 12px;background:#fef3c7;border-top:1px solid #fde68a;border-bottom:1px solid #fde68a;">
           <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
           <span style="font-size:11px;color:#92400e;font-weight:500;">${hData.absenceHours}h absence${hData.absenceReasons.length ? ` — ${hData.absenceReasons.join(', ')}` : ''}</span>
         </div>`
      : '';

    // ── Conflict banner ───────────────────────────────────────────────────────
    // Encode ALL booking IDs on the resolve button so the click handler can
    // pass every conflicting booking to the Batch Assignment modal at once.
    const allConflictIds = hData.bookings.map(b => b.id).join(',');
    const conflictCount = hData.bookings.length;
    const conflictBanner = hData.hasHardConflict
      ? `<div style="display:flex;align-items:center;gap:6px;padding:6px 12px;background:#fffbeb;border-bottom:1px solid #fde68a;">
           <div style="display:flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:#f59e0b;flex-shrink:0;">
             <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
           </div>
           <div style="flex:1;min-width:0;">
             <div style="font-size:11px;color:#92400e;font-weight:700;line-height:1.3;">Scheduling Conflict</div>
           </div>
           <button
             data-heatmap-resolve-bookings="${allConflictIds}"
             style="flex-shrink:0;display:inline-flex;align-items:center;gap:3px;padding:3px 9px;font-size:10px;font-weight:700;color:#ffffff;background:#f59e0b;border:1px solid #d97706;border-radius:4px;cursor:pointer;white-space:nowrap;line-height:1.6;"
             onmouseover="this.style.background='#d97706'"
             onmouseout="this.style.background='#f59e0b'"
           >
             <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
             Resolve ${conflictCount > 1 ? `(${conflictCount})` : ''}
           </button>
         </div>`
      : '';

    // ── Weekly context bar ────────────────────────────────────────────────────
    const weeklyBar = hData.weeklyAvgUtilization !== null
      ? (() => {
          const pct = Math.min(hData.weeklyAvgUtilization, 100);
          const barColor = pct > 100 ? '#fecaca' : pct >= 91 ? '#fde68a' : pct >= 71 ? '#bbf7d0' : '#bfdbfe';
          return `
            <div style="padding:6px 12px 7px;border-top:1px solid rgba(0,0,0,0.06);background:#f9fafb;">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px;">
                <span style="font-size:10px;font-weight:600;color:#9ca3af;letter-spacing:0.04em;text-transform:uppercase;">Avg utilisation this week</span>
                <span style="font-size:11px;font-weight:700;color:rgba(0,0,0,0.75);">${hData.weeklyAvgUtilization}%</span>
              </div>
              <div style="height:4px;background:#e5e7eb;border-radius:2px;overflow:hidden;">
                <div style="height:100%;width:${pct}%;background:${barColor};border-radius:2px;transition:width 0.2s;"></div>
              </div>
            </div>`;
        })()
      : '';

    // ── Summary stats strip ───────────────────────────────────────────────────
    // Use the reconciled total (confirmedHours + plannedHours) so the numbers
    // are always internally consistent. Label the middle column contextually:
    //   • Planned only  → "Planned"   (no confirmed hours at all)
    //   • Mixed         → "Total"     (confirmed + planned combined)
    //   • Confirmed     → "Booked"    (all confirmed, classic label)
    const totalHours   = Math.round((hData.confirmedHours + hData.plannedHours) * 10) / 10;
    const bookedLabel  = hData.isPlannedOnly ? 'Planned' : hData.isMixed ? 'Total' : 'Booked';
    const bookedValue  = hData.isPlannedOnly ? hData.plannedHours : hData.isMixed ? totalHours : hData.confirmedHours;
    const remainingVal = Math.round((hData.availableHours - totalHours) * 10) / 10;

    const summaryStrip = `
      <div style="display:flex;gap:0;border-top:1px solid rgba(0,0,0,0.08);border-bottom:1px solid rgba(0,0,0,0.08);background:#f9fafb;">
        <div style="flex:1;text-align:center;padding:6px 4px;border-right:1px solid rgba(0,0,0,0.08);">
          <div style="font-size:11px;color:#6b7280;margin-bottom:1px;">Available</div>
          <div style="font-size:13px;font-weight:600;color:#1f2937;">${hData.availableHours}h</div>
        </div>
        <div style="flex:1;text-align:center;padding:6px 4px;border-right:1px solid rgba(0,0,0,0.08);">
          <div style="font-size:11px;color:#6b7280;margin-bottom:1px;">${bookedLabel}</div>
          <div style="font-size:13px;font-weight:600;color:#1f2937;">${bookedValue}h</div>
        </div>
        <div style="flex:1;text-align:center;padding:6px 4px;">
          <div style="font-size:11px;color:#6b7280;margin-bottom:1px;">${remainingVal < 0 ? 'Overbooked' : 'Remaining'}</div>
          <div style="font-size:13px;font-weight:600;color:${remainingVal < 0 ? '#b91c1c' : '#1f2937'};">${remainingVal < 0 ? `+${Math.abs(remainingVal)}h` : `${remainingVal}h`}</div>
        </div>
      </div>
      ${forecastBanner ? '' : confirmedPlannedDetail}`;

    // Booking rows
    let bookingRows = '';
    if (hData.bookings.length === 0) {
      bookingRows = `<div style="padding:10px 12px;font-size:12px;color:#9ca3af;font-style:italic;">No bookings</div>`;
    } else {
      hData.bookings.forEach((b, idx) => {
        const isLast = idx === hData.bookings.length - 1;
        const timeStr = (b.durationType === 'full-day')
          ? 'All day'
          : (b.durationType === 'half-day' || b.durationType === 'half-day-am')
            ? `${fmtTime(b.startTime)} – midday (AM)`
            : b.durationType === 'half-day-pm'
              ? `Midday – ${fmtTime(b.endTime)} (PM)`
              : `${fmtTime(b.startTime)} – ${fmtTime(b.endTime)}`;
        const duration = fmtDuration(b.startTime, b.endTime, b.durationType);

        bookingRows += `
          <div style="padding:8px 12px;${isLast ? '' : 'border-bottom:1px solid #f3f4f6;'}display:flex;align-items:center;gap:8px;">
            <!-- Project + status -->
            <div style="flex:1;min-width:0;">
              <div style="font-size:12px;font-weight:600;color:#111827;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${b.projectName || b.description || 'Booking'}</div>
              <div style="display:flex;align-items:center;gap:4px;margin-top:1px;">
                ${statusDot(b.status)}<span style="font-size:10px;color:#6b7280;">${capitalize(b.status || '')}</span>
                <span style="font-size:10px;color:#d1d5db;">·</span>
                <span style="font-size:10px;color:#6b7280;">${timeStr}</span>
              </div>
            </div>
            <!-- Edit button -->
            <button
              data-heatmap-edit-booking="${b.id}"
              style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;font-size:10px;font-weight:500;color:#3b82f6;background:#eff6ff;border:1px solid #bfdbfe;border-radius:4px;cursor:pointer;white-space:nowrap;flex-shrink:0;"
              onmouseover="this.style.background='#dbeafe'"
              onmouseout="this.style.background='#eff6ff'"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </button>
          </div>`;
      });
    }

    const dateLabel = format(tickStart, calendarView === 'week' || calendarView === 'month' ? 'EEE d MMM yyyy' : calendarView === 'day' ? 'EEE d MMM yyyy, HH:mm' : 'd MMM yyyy');

    return `
      <div style="width:300px;max-width:min(300px,calc(100vw - 32px));font-family:inherit;border-radius:8px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.15);display:flex;flex-direction:column;">
        <!-- Header -->
        <div style="background:${hData.bandColor};padding:10px 12px;">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:2px;">
            <div style="display:flex;align-items:center;gap:5px;">
              ${hData.hasHardConflict ? `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="${headerTextColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>` : ''}
              <span style="font-size:13px;font-weight:700;color:${headerTextColor};">${hData.band}</span>
            </div>
            <span style="font-size:13px;font-weight:700;color:${headerTextColor};">${hData.utilization}%</span>
          </div>
          <div style="font-size:11px;color:${headerTextColor};opacity:0.85;">${dateLabel} &nbsp;·&nbsp; ${hData.bookedHours}h booked</div>
        </div>
        <!-- Forecast / planned-only banner (blue = low risk, amber = high risk) -->
        ${forecastBanner}
        <!-- Summary strip -->
        ${summaryStrip}
        <!-- Absence row (amber) -->
        ${absenceRow}
        <!-- Conflict banner (red) -->
        ${conflictBanner}
        <!-- Bookings -->
        <div style="flex:1 1 auto;min-height:0;max-height:340px;overflow-y:auto;background:#ffffff;">
          ${hData.bookings.length > 0 ? `<div style="padding:7px 12px 3px;font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:0.06em;text-transform:uppercase;display:flex;align-items:center;gap:5px;"><span style="display:inline-flex;align-items:center;justify-content:center;min-width:16px;height:16px;border-radius:8px;background:#e5e7eb;color:#6b7280;font-size:10px;font-weight:700;padding:0 4px;">${hData.bookings.length}</span>${hData.bookings.length !== 1 ? 'Bookings' : 'Booking'}</div>` : ''}
          ${bookingRows}
        </div>
        <!-- Weekly context bar -->
        ${weeklyBar}
      </div>`;
  }, [calendarView]);
  // ─────────────────────────────────────────────────────────────────────────────

  const generateTooltipHtml = useCallback((booking: Booking): string => {
    const bookingType = settings.bookingTypes.find((bt: BookingType) => String(bt.id) === String(booking.bookingTypeId));

    let fieldConfigurations: FieldConfiguration[] = [];
    if (bookingType?.fieldConfigurations) {
      try {
        fieldConfigurations = typeof bookingType.fieldConfigurations === 'string'
          ? JSON.parse(bookingType.fieldConfigurations)
          : bookingType.fieldConfigurations || [];
      } catch (error) {
        console.error('Error parsing field configurations:', error);
      }
    }

    const getFieldValue = (fieldName: string): string | null => {
      const userCharMatch = fieldName.match(/^User Char (\d+)$/);
      if (userCharMatch) {
        const index = parseInt(userCharMatch[1], 10);
        return booking.userChar?.[index] || null;
      }

      const userNumberMatch = fieldName.match(/^User Number (\d+)$/);
      if (userNumberMatch) {
        const index = parseInt(userNumberMatch[1], 10);
        return booking.userNumber?.[index] || null;
      }

      const userFlagMatch = fieldName.match(/^User Flag (\d+)$/);
      if (userFlagMatch) {
        const index = parseInt(userFlagMatch[1], 10);
        const value = booking.userFlag?.[index];
        if (value === undefined || value === null) return null;
        return value ? 'Yes' : 'No';
      }

      const userDateMatch = fieldName.match(/^User Date (\d+)$/);
      if (userDateMatch) {
        const index = parseInt(userDateMatch[1], 10);
        const value = booking.userDate?.[index];
        if (!value) return null;
        try {
          const date = new Date(value);
          if (isValid(date)) return format(date, 'PPP');
        } catch (e) {
          return String(value);
        }
        return null;
      }

      const userTimeMatch = fieldName.match(/^User Time (\d+)$/);
      if (userTimeMatch) {
        const index = parseInt(userTimeMatch[1], 10);
        const value = booking.userTime?.[index];
        if (!value) return null;
        try {
          const time = new Date(value);
          if (isValid(time)) return format(time, 'p');
        } catch (e) {
          return String(value);
        }
        return null;
      }

      const userNotesMatch = fieldName.match(/^User Notes (\d+)$/);
      if (userNotesMatch) {
        const index = parseInt(userNotesMatch[1], 10);
        return booking.userNotes?.[index] || null;
      }

      const fieldMap: { [key: string]: string } = {
        'Project': 'projectName',
        'Customer': 'customer',
        'Description': 'description',
        'Location': 'location',
        'Details': 'details',
        'Booking Reference': 'bookingReference',
        'Cost Centre': 'costCentre',
        'Booking Notes': 'bookingNotes',
        'Task Description': 'taskDescription',
        'Work Type': 'workType',
        'Colour': 'color',
        'Priority': 'priority',
        'Billing Classification': 'billingClassification',
      };

      const bookingField = fieldMap[fieldName];
      if (!bookingField) return null;

      let value = (booking as unknown as Record<string, unknown>)[bookingField];

      // Handle special cases - lookup names from IDs
      if (fieldName === 'Project' && !value && booking.projectId) {
        const project = projectDetails.find((p) => String(p.id) === String(booking.projectId));
        value = project?.name;
      }

      if (fieldName === 'Customer' && !value && booking.customer) {
        const customer = clients.find((c) => String(c.id) === String(booking.customer));
        value = customer?.name;
      }

      if (fieldName === 'Cost Centre' && !value && booking.costCentreId) {
        const costCentre = costCentres.find((cc) => String(cc.id) === String(booking.costCentreId));
        value = costCentre?.name;
      }

      if (value === undefined || value === null || value === '') return null;
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      return String(value);
    };

    const header = booking.description || booking.projectName || 'Booking';

    const startTime = booking.startTime ? new Date(booking.startTime) : null;
    const endTime = booking.endTime ? new Date(booking.endTime) : null;
    const isValidStartTime = startTime && isValid(startTime);
    const isValidEndTime = endTime && isValid(endTime);

    // Shift tooltip times by the offset between booking timezone and header timezone
    const sourceTzTooltip = (booking as any).timezone || settings.timelineDefaultTimezone || 'UTC';
    const targetTzTooltip = selectedTimezone || sourceTzTooltip;
    const tooltipOffsetMin = getTimezoneOffset(sourceTzTooltip, targetTzTooltip);
    let tooltipStart = isValidStartTime
      ? new Date((startTime as Date).getTime() + tooltipOffsetMin * 60000)
      : null;
    let tooltipEnd = isValidEndTime
      ? new Date((endTime as Date).getTime() + tooltipOffsetMin * 60000)
      : null;
    if (booking.durationType === 'half-day' || booking.durationType === 'half-day-am' || booking.durationType === 'half-day-pm') {
      if (isValidStartTime) {
        const day = new Date(startTime as Date);
        day.setHours(0, 0, 0, 0);
        const businessStart = settings.businessHoursStart ?? 9;
        const businessEnd = settings.businessHoursEnd ?? 17;
        const period = booking.durationType === 'half-day'
          ? (booking.halfDayPeriod === 'pm' ? 'pm' : 'am')
          : (booking.durationType === 'half-day-am' ? 'am' : 'pm');
        if (period === 'am') {
          tooltipStart = new Date(day);
          tooltipStart.setHours(businessStart, 0, 0, 0);
          tooltipEnd = new Date(day);
          tooltipEnd.setHours(12, 0, 0, 0);
        } else {
          tooltipStart = new Date(day);
          tooltipStart.setHours(12, 0, 0, 0);
          tooltipEnd = new Date(day);
          tooltipEnd.setHours(businessEnd, 0, 0, 0);
        }
      }
    }

    const statusConfig = settings.statusConfigs?.find((s) => String(s.id) === String(booking.status));
    const statusColor = statusConfig?.color || '#6b7280';
    const statusText = statusConfig?.customText || statusConfig?.text || booking.status;

    const tooltipFields = fieldConfigurations.filter((field: FieldConfiguration) =>
      field.showTooltip === true &&
      !['From', 'To', 'Status', 'from', 'to', 'status'].includes(field.fieldName)
    );

    let html = `<div style="padding: 10px; max-width: 340px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">`;

    html += `<div style="font-weight: 600; font-size: 14px; margin-bottom: 6px; color: #1f2937;">${header}</div>`;

    if (tooltipStart && isValid(tooltipStart)) {
      html += `<div style="display: flex; flex-direction: column; gap: 2px; font-size: 12px; color: #6b7280; margin-bottom: 8px;">`;
      html += `<div style="display: flex; align-items: center; gap: 6px;">`;
      html += `<span style="color: #9ca3af;">From:</span>`;
      html += `<span>${format(tooltipStart, 'EEE, MMM d, yyyy')} at ${format(tooltipStart, 'h:mm a')}</span>`;
      html += `</div>`;
      if (tooltipEnd && isValid(tooltipEnd)) {
        html += `<div style="display: flex; align-items: center; gap: 6px;">`;
        html += `<span style="color: #9ca3af;">To:</span>`;
        html += `<span>${format(tooltipEnd, 'EEE, MMM d, yyyy')} at ${format(tooltipEnd, 'h:mm a')}</span>`;
        html += `</div>`;
      }
      html += `</div>`;
    }

    if (statusText) {
      html += `<div style="margin-bottom: 8px;">`;
      html += `<span style="display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 9999px; font-size: 11px; font-weight: 500; background-color: ${statusColor}15; color: ${statusColor}; border: 1px solid ${statusColor}30;">`;
      html += `<span style="width: 6px; height: 6px; border-radius: 50%; background-color: ${statusColor};"></span>`;
      html += `${statusText}`;
      html += `</span></div>`;
    }

    if (tooltipFields.length > 0) {
      html += `<div style="border-top: 1px solid #f3f4f6; padding-top: 8px; margin-top: 4px;">`;
      for (const field of tooltipFields) {
        const fieldValue = getFieldValue(field.fieldName);
        if (fieldValue) {
          const fieldLabel = field.customName || field.fieldName;
          html += `<div style="display: flex; gap: 8px; font-size: 11px; margin-bottom: 4px; line-height: 1.4;">`;
          html += `<span style="color: #9ca3af; min-width: 90px; flex-shrink: 0;">${fieldLabel}:</span>`;
          html += `<span style="color: #374151; flex: 1; word-break: break-word;">${fieldValue}</span>`;
          html += `</div>`;
        }
      }
      html += `</div>`;
    }

    html += `</div>`;
    return html;
  }, [settings.bookingTypes, settings.statusConfigs, settings.timelineDefaultTimezone, settings.businessHoursStart, settings.businessHoursEnd, projects, projectDetails, clients, costCentres, selectedTimezone]);

  const generateTooltipHtmlRef = useRef(generateTooltipHtml);
  generateTooltipHtmlRef.current = generateTooltipHtml;
  const generateHeatmapTooltipHtmlRef = useRef(generateHeatmapTooltipHtml);
  generateHeatmapTooltipHtmlRef.current = generateHeatmapTooltipHtml;
  const findBookingByIdRef = useRef(findBookingById);
  findBookingByIdRef.current = findBookingById;

  const handleEventClick = useCallback(({ eventRecord }: { eventRecord: { data: BryntumEvent } }) => {
    const booking = findBookingById(eventRecord.data.id);
    if (booking && onEditBooking) {
      onEditBooking(booking);
    }
  }, [findBookingById, onEditBooking]);

  const handleEventDblClick = useCallback(({ eventRecord }: { eventRecord: { data: BryntumEvent } }) => {
    const booking = findBookingById(eventRecord.data.id);
    if (booking && onEditBooking) {
      onEditBooking(booking);
    }
    // Return false to prevent Bryntum's default event edit behavior
    return false;
  }, [findBookingById, onEditBooking]);

  const handleScheduleClick = useCallback(({ resourceRecord, date }: { resourceRecord: { data: BryntumResource }; date: Date }) => {
    const id = resourceRecord.data.id as unknown as string;
    // Block interactions on grouping headers (group-, project-, role-, customer-)
    if (id?.startsWith('group-') || id?.startsWith('project-') || id?.startsWith('role-') || id?.startsWith('customer-')) return false as unknown as void;

    const resource = findResourceById(id);
    if (resource) {
      if (onSlotClick) {
        // Extract projectId from combined ID in project-resource mode
        // Combined IDs are in format: "projectId-resourceId"
        let bookingData: Partial<Booking> | undefined;
        const combinedId = id;
        if (groupingMode === 'project-resource' && combinedId.includes('-') && !combinedId.startsWith('group-') && !combinedId.startsWith('project-')) {
          const parts = combinedId.split('-');
          if (parts.length >= 2) {
            const projectId = parts[0];
            bookingData = { projectId };
          }
        }

        onSlotClick(resource, date, { hour: date.getHours(), minute: date.getMinutes() }, bookingData);
      }
    }
  }, [findResourceById, onSlotClick, groupingMode, isNonWorkingDay, isNonBusinessHour]);

  const handleScheduleDblClick = useCallback(({ resourceRecord, eventRecord, date }: { resourceRecord: { data: BryntumResource }; date: Date, eventRecord: { startDate: Date; endDate: Date; remove?: () => void }; }) => {
    const id = resourceRecord.data.id as unknown as string;
    // Block interactions on grouping headers (group-, project-, role-, customer-)
    if (id?.startsWith('group-') || id?.startsWith('project-') || id?.startsWith('role-') || id?.startsWith('customer-')) return false as unknown as void;

    const resource = findResourceById(id);
    if (resource && onDragSelect) {
      cleanupPhantomEvents();
      let endTime: Date;

      // For Year view, set end time to the last day of the clicked month
      if (calendarView === 'year') {
        endTime = new Date(date.getFullYear(), date.getMonth() + 1, 0); // Last day of month
      } else {
        endTime = new Date(date);
        endTime.setHours(endTime.getHours() + 1);
      }

      // Extract projectId from combined ID in project-resource mode
      // Combined IDs are in format: "projectId-resourceId"
      let bookingData: Partial<Booking> | undefined;
      const combinedId = resourceRecord.data.id;
      if (groupingMode === 'project-resource' && combinedId.includes('-') && !combinedId.startsWith('group-') && !combinedId.startsWith('project-')) {
        const parts = combinedId.split('-');
        if (parts.length >= 2) {
          const projectId = parts[0];
          bookingData = { projectId };
        }
      }

      // DYNAMIC GRANULARITY DETECTION:
      // Determine durationType based on the timeline's zoom level and calendar view.
      // This ensures the booking form opens with the correct duration type based on
      // what the user sees on the timeline, not just the nominal view mode.

      // Determine if the timeline is showing hourly granularity based on calendarView and zoomLevel:
      // - Day view: always hourly (zoomLevel 0 = hourly, zoomLevel 1 = 15-min)
      // - Week/Month view: zoomLevel 0 = daily, zoomLevel 1-4 = hourly
      // - Quarter view: weekly granularity (not hourly)
      // - Year view: monthly granularity (not hourly)
      const isHourlyGranularity =
        calendarView === 'day' || // Day view is always hourly
        ((calendarView === 'week' || calendarView === 'month') && zoomLevel >= 1); // Week/Month zoomed in

      if (isHourlyGranularity) {
        bookingData = { ...bookingData, durationType: 'hourly' };
      }

      // Clean up any remaining phantom events as a safety net
      cleanupPhantomEvents();

      // Otherwise, let the handler decide based on the view type (will default to 'full-day')

      onDragSelect(
        resource,
        date,
        { hour: date.getHours(), minute: date.getMinutes() },
        { hour: endTime.getHours(), minute: endTime.getMinutes() },
        bookingData,
        endTime
      );
    }
    // Always block Bryntum's default create-on-dblclick
    return false as unknown as void;
  }, [findResourceById, onDragSelect, calendarView, zoomLevel, groupingMode, isNonWorkingDay, isNonBusinessHour]);

  const handleEventDrop = useCallback(({ eventRecords, context }: { eventRecords: Array<{ data: BryntumEvent }>; context: { newResource?: { data: BryntumResource }; startDate: Date; endDate: Date } }) => {
    if (eventRecords.length === 0) return;

    const eventRecord = eventRecords[0];
    const booking = findBookingById(eventRecord.data.id);
    // Use booking.resourceId from React state (not updated yet) to get the ORIGINAL resource
    // eventRecord.data.resourceId might have been updated by Bryntum after the drag
    const originalResource = booking ? findResourceById(booking.resourceId ?? "") : null;
    const newResourceId = context.newResource?.data.id || eventRecord.data.resourceId;

    // VALIDATION: Prevent dropping bookings on group/parent nodes
    // Group nodes have IDs that start with "group-" (e.g., "group-Department Name")
    // or "project-" (e.g., "project-123" for project groups in project-resource mode)
    // or "role-" (e.g., "role-Developer" for role groups in role-resource mode)
    // Only actual resources (leaf nodes) should accept bookings
    const isGroupNode = newResourceId.startsWith('group-') || newResourceId.startsWith('project-') || newResourceId.startsWith('role-') || newResourceId.startsWith('customer-');
    if (isGroupNode) {
      console.log('❌ Invalid drop target: Cannot drop booking on group node', {
        bookingId: booking?.id,
        groupId: newResourceId,
      });

      // Revert the booking to its original position
      if (booking && originalResource) {
        const originalStartTime = new Date(booking.startTime);
        const originalEndTime = new Date(booking.endTime);

        // Determine the correct resource ID to revert to based on grouping mode
        let revertResourceId: string;
        if (groupingMode === 'project-resource' && booking.projectId) {
          revertResourceId = `${booking.projectId}-${originalResource.id}`;
        } else if (groupingMode === 'role-resource') {
          const roleName = originalResource.role || 'Unassigned';
          revertResourceId = `${roleName}-${originalResource.id}`;
        } else if (groupingMode === 'customer') {
          let custName: string = booking.customer?.trim() || '';
          if (!custName && booking.projectId) {
            const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
            custName = project?.clientName?.trim() || '';
          }
          if (!custName) custName = 'Unassigned';
          revertResourceId = `${custName}-${originalResource.id}`;
        } else {
          revertResourceId = originalResource.id;
        }

        // Use the exposed revertEvent method to restore the booking
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const eventStore = scheduler.eventStore;
          const eventToRevert = eventStore.getById(booking.id);

          if (eventToRevert) {
            eventToRevert.beginBatch();
            (eventToRevert as any).startDate = originalStartTime;
            (eventToRevert as any).endDate = originalEndTime;
            (eventToRevert as any).resourceId = revertResourceId;
            eventToRevert.endBatch();
          }
        }
      }

      // Return early without calling onBookingDragEnd
      return;
    }

    // In project-resource or role-resource mode, extract the actual resource ID from the combined ID
    // Combined IDs are in format: "projectId-resourceId" or "roleName-resourceId"
    let actualNewResourceId = newResourceId;
    if (groupingMode === 'project-resource' && newResourceId.includes('-') && !newResourceId.startsWith('group-')) {
      // Extract the resource ID from the combined project-resource ID
      const parts = newResourceId.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (project ID)
        actualNewResourceId = parts.slice(1).join('-');
      }
    } else if (groupingMode === 'role-resource' && newResourceId.includes('-') && !newResourceId.startsWith('group-') && !newResourceId.startsWith('role-')) {
      // Extract the resource ID from the combined role-resource ID
      const parts = newResourceId.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (role name)
        actualNewResourceId = parts.slice(1).join('-');
      }
    } else if (groupingMode === 'customer' && newResourceId.includes('-') && !newResourceId.startsWith('group-') && !newResourceId.startsWith('customer-')) {
      // Extract the resource ID from the combined customer-resource ID
      // Try matching by suffix first (handles hyphens in customer names)
      const matchedResource = resources.find((r: Resource) => {
        const rid = String(r.id);
        return newResourceId.endsWith(`-${rid}`);
      });
      if (matchedResource) {
        actualNewResourceId = String(matchedResource.id);
      } else {
        // Fallback: everything after first hyphen
        const dashIdx = newResourceId.indexOf('-');
        if (dashIdx !== -1) actualNewResourceId = newResourceId.slice(dashIdx + 1);
      }
    }

    // VALIDATION: Prevent dropping bookings on non-working days
    // Check if the target date falls on a non-working day for the target resource
    const targetResource = findResourceById(actualNewResourceId);
    if (targetResource && booking && originalResource) {
      const targetDate = context.startDate;

      // Check non-working day first
      if (isNonWorkingDay(targetResource, targetDate)) {
        console.log('❌ Invalid drop target: Cannot drop booking on non-working day', {
          bookingId: booking.id,
          resourceId: actualNewResourceId,
          targetDate: targetDate.toISOString(),
        });

        // Revert the booking to its original position
        const originalStartTime = new Date(booking.startTime);
        const originalEndTime = new Date(booking.endTime);

        // Determine the correct resource ID to revert to based on grouping mode
        let revertResourceId: string;
        if (groupingMode === 'project-resource' && booking.projectId) {
          revertResourceId = `${booking.projectId}-${originalResource.id}`;
        } else if (groupingMode === 'role-resource') {
          const roleName = originalResource.role || 'Unassigned';
          revertResourceId = `${roleName}-${originalResource.id}`;
        } else {
          revertResourceId = originalResource.id;
        }

        // Use the scheduler to restore the booking
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const eventStore = scheduler.eventStore;
          const eventToRevert = eventStore.getById(booking.id);

          if (eventToRevert) {
            eventToRevert.beginBatch();
            (eventToRevert as any).startDate = originalStartTime;
            (eventToRevert as any).endDate = originalEndTime;
            (eventToRevert as any).resourceId = revertResourceId;
            eventToRevert.endBatch();
          }
        }

        // Return early without calling onBookingDragEnd
        return;
      }

      // VALIDATION: Prevent dropping bookings during non-business hours (Day view)
      if (isNonBusinessHour(targetDate)) {
        console.log('❌ Invalid drop target: Cannot drop booking during non-business hours', {
          bookingId: booking.id,
          resourceId: actualNewResourceId,
          targetDate: targetDate.toISOString(),
        });

        // Revert the booking to its original position
        const originalStartTime = new Date(booking.startTime);
        const originalEndTime = new Date(booking.endTime);

        let revertResourceId: string;
        if (groupingMode === 'project-resource' && booking.projectId) {
          revertResourceId = `${booking.projectId}-${originalResource.id}`;
        } else if (groupingMode === 'role-resource') {
          const roleName = originalResource.role || 'Unassigned';
          revertResourceId = `${roleName}-${originalResource.id}`;
        } else {
          revertResourceId = originalResource.id;
        }

        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const eventStore = scheduler.eventStore;
          const eventToRevert = eventStore.getById(booking.id);
          if (eventToRevert) {
            eventToRevert.beginBatch();
            (eventToRevert as any).startDate = originalStartTime;
            (eventToRevert as any).endDate = originalEndTime;
            (eventToRevert as any).resourceId = revertResourceId;
            eventToRevert.endBatch();
          }
        }

        return;
      }
    }

    if (booking && originalResource && onBookingDragEnd) {
      setTimeout(() => {
        onBookingDragEnd(
          booking,
          originalResource,
          actualNewResourceId,
          context.startDate,
          context.endDate,
          false
        );
      }, 0);
    }
  }, [findBookingById, findResourceById, onBookingDragEnd, groupingMode, isNonWorkingDay, isNonBusinessHour]);

  const handleEventResizeEnd = useCallback(({ eventRecord }: { eventRecord: { data: BryntumEvent; startDate: Date; endDate: Date } }) => {
    const booking = findBookingById(eventRecord.data.id);
    const resource = findResourceById(eventRecord.data.resourceId);

    console.log('🔄 handleEventResizeEnd called:', {
      bookingId: eventRecord.data.id,
      newStartDate: (eventRecord as any).startDate,
      newEndDate: (eventRecord as any).endDate,
    });

    if (booking && resource && onBookingDragEnd) {
      setTimeout(() => {
        onBookingDragEnd(
          booking,
          resource,
          booking.resourceId ?? "",
          (eventRecord as any).startDate,
          (eventRecord as any).endDate,
          true
        );
      }, 0);
    }
  }, [findBookingById, findResourceById, onBookingDragEnd]);

  const handleDragCreateEnd = useCallback(({ resourceRecord, eventRecord, context }: { resourceRecord: { data: BryntumResource }; eventRecord: { startDate: Date; endDate: Date; remove?: () => void }; context?: { finalize?: (createEvent: boolean) => void } }) => {
    const id = resourceRecord.data.id as unknown as string;
    // Block drag-create on grouping headers (group-, project-, role-, customer-)
    if (id?.startsWith('group-') || id?.startsWith('project-') || id?.startsWith('role-') || id?.startsWith('customer-')) {
      if (context?.finalize) context.finalize(false);
      if (eventRecord.remove) eventRecord.remove();
      cleanupPhantomEvents();
      return false as unknown as void;
    }

    const resource = findResourceById(id);
    if (resource && onDragSelect) {
      const startDate = (eventRecord as any).startDate;



      let endDate = (eventRecord as any).endDate;

      // For non-day views, Bryntum snaps to the START of the next period:
      // - Week/Month views (day snapping): Mon 00:00 → Tue 00:00 for single day
      // - Quarter view (week snapping): Mon 00:00 → next Mon 00:00 for single week
      // - Year view (month snapping): Jan 1 00:00 → Feb 1 00:00 for single month
      // We subtract one day from endDate to get the actual last day of the booking
      if (calendarView !== 'day') {
        if (endDate.getHours() === 0 && endDate.getMinutes() === 0) {
          const adjustedEndDate = new Date(endDate);
          adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
          endDate = adjustedEndDate;
        }
      }

      // Extract projectId from combined ID in project-resource mode
      // Combined IDs are in format: "projectId-resourceId"
      let bookingData: Partial<Booking> | undefined;
      const combinedId = resourceRecord.data.id;
      if (groupingMode === 'project-resource' && combinedId.includes('-') && !combinedId.startsWith('group-') && !combinedId.startsWith('project-')) {
        const parts = combinedId.split('-');
        if (parts.length >= 2) {
          const projectId = parts[0];
          bookingData = { projectId };
        }
      }

      // DYNAMIC GRANULARITY DETECTION:
      // Determine durationType based on the ACTUAL timeline granularity (from viewPreset)
      // rather than just the nominal view mode. This allows for zoom-aware pre-selection.

      // Get the actual time resolution from the current viewPreset
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      const currentViewPreset = scheduler?.viewPreset as { timeResolution?: { unit?: string; increment?: number } } | undefined;
      const timeResolutionUnit = currentViewPreset?.timeResolution?.unit;

      // Determine if we should use hourly duration type:
      // Only when the timeline is displaying hourly granularity (hour or minute units)
      // Cross-day drag in day-level views (month, week, quarter) should remain full-day
      const isHourlyGranularity = timeResolutionUnit === 'hour' || timeResolutionUnit === 'minute';

      if (isHourlyGranularity) {
        bookingData = { ...bookingData, durationType: 'hourly' };
      }
      // Otherwise, let the handler decide based on the view type (will default to 'full-day')

      onDragSelect(
        resource,
        startDate,
        { hour: startDate.getHours(), minute: startDate.getMinutes() },
        { hour: endDate.getHours(), minute: endDate.getMinutes() },
        bookingData,
        endDate
      );
    }
    // Prevent Bryntum from finalizing the event creation
    // This tells Bryntum not to add the temporary event to the store
    if (context?.finalize) {
      context.finalize(false);
    }
    // Also explicitly remove the temporary event if it was added
    // This ensures the event is removed even if finalize doesn't work as expected
    if (eventRecord.remove) {
      eventRecord.remove();
    }
    // Clean up any remaining phantom events as a safety net
    cleanupPhantomEvents();
    // Return false to prevent the event from being added
    return false;
  }, [findResourceById, onDragSelect, calendarView, zoomLevel, groupingMode, cleanupPhantomEvents, isNonWorkingDay, isNonBusinessHour]);

  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    // Validate dates before setting time span
    const isStartValid = startDate instanceof Date && !isNaN(startDate.getTime());
    const isEndValid = endDate instanceof Date && !isNaN(endDate.getTime());
    const isOrderValid = isStartValid && isEndValid && startDate.getTime() < endDate.getTime();

    console.log('🕝 useEffect setTimeSpan - Date validation:', {
      isStartValid,
      isEndValid,
      isOrderValid,
      startDate: startDate?.toString(),
      endDate: endDate?.toString(),
      startTime: startDate?.getTime(),
      endTime: endDate?.getTime()
    });

    if (isOrderValid) {
      console.log('✅ Setting time span:', { startDate: startDate.toString(), endDate: endDate.toString() });
      scheduler.setTimeSpan(startDate, endDate);
    } else {
      console.error('❌ Cannot set time span: invalid date range', {
        startDate: startDate?.toString(),
        endDate: endDate?.toString(),
        reason: !isStartValid ? 'Invalid start' : !isEndValid ? 'Invalid end' : 'Start >= End'
      });
    }
  }, [startDate, endDate]);


  // Attach click handler to expand/collapse all button
  useEffect(() => {
    const handleExpandCollapseClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const button = target.closest('#expand-collapse-all-btn');
      console.log('Click detected, target:', target, 'button found:', button);
      if (button) {
        console.log('Expand/Collapse button clicked!');
        e.preventDefault();
        e.stopPropagation();
        toggleAllGroups();
      }
    };

    document.addEventListener('click', handleExpandCollapseClick, true);
    return () => {
      document.removeEventListener('click', handleExpandCollapseClick, true);
    };
  }, [toggleAllGroups]);

  // Delegated click handler for "Edit" buttons inside heatmap tooltip booking rows
  const onEditBookingRef = useRef(onEditBooking);
  onEditBookingRef.current = onEditBooking;

  // Delegated click handler for "Resolve" buttons inside heatmap tooltip booking rows
  const onResolveConflictRef = useRef(onResolveConflict);
  onResolveConflictRef.current = onResolveConflict;

  useEffect(() => {
    const handleHeatmapEditClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('[data-heatmap-edit-booking]') as HTMLElement | null;
      if (!btn) return;
      const bookingId = btn.getAttribute('data-heatmap-edit-booking');
      if (!bookingId) return;
      e.preventDefault();
      e.stopPropagation();
      const booking = bookings.find(bk => String(bk.id) === String(bookingId));
      if (booking && onEditBookingRef.current) {
        // Hide the Bryntum tooltip first so it doesn't linger behind the modal
        const scheduler = schedulerRef.current?.instance as any;
        scheduler?.features?.eventTooltip?.tooltip?.hide?.();
        onEditBookingRef.current(booking);
      }
    };
    document.addEventListener('click', handleHeatmapEditClick, true);
    return () => {
      document.removeEventListener('click', handleHeatmapEditClick, true);
    };
  }, [bookings]);

  useEffect(() => {
    const handleHeatmapResolveClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('[data-heatmap-resolve-bookings]') as HTMLElement | null;
      if (!btn) return;
      const bookingIdsAttr = btn.getAttribute('data-heatmap-resolve-bookings');
      if (!bookingIdsAttr) return;
      e.preventDefault();
      e.stopPropagation();
      // Decode the comma-separated list of IDs and look up each booking
      const ids = bookingIdsAttr.split(',').map(id => id.trim()).filter(Boolean);
      const cellBookings = ids
        .map(id => bookings.find(bk => String(bk.id) === id))
        .filter((bk): bk is Booking => bk !== undefined);
      if (cellBookings.length > 0 && onResolveConflictRef.current) {
        // Hide the Bryntum tooltip first so it doesn't linger behind the dialog
        const scheduler = schedulerRef.current?.instance as any;
        scheduler?.features?.eventTooltip?.tooltip?.hide?.();
        onResolveConflictRef.current(cellBookings);
      }
    };
    document.addEventListener('click', handleHeatmapResolveClick, true);
    return () => {
      document.removeEventListener('click', handleHeatmapResolveClick, true);
    };
  }, [bookings]);



  // Ensure createEventOnDblClick is disabled after every mount/remount.
  // After navigating away from the calendar and back, the Bryntum React wrapper
  // may not properly re-apply the createEventOnDblClick config from props.
  // This useEffect explicitly sets it on the scheduler instance.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    (scheduler as any).createEventOnDblClick = false;
  }, []);


  // Prevent Bryntum's default event edit form from opening
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    // Listen for beforeEventEdit to prevent the default edit form
    const detacher = scheduler.on('beforeEventEdit', () => {
      // Return false to prevent Bryntum's event edit form from opening
      return false;
    });

    return () => {
      if (typeof detacher === 'function') {
        detacher();
      }
    };
  }, []);

  // Prevent right-click context menu on timeline header
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    // Aggressively disable header-related menu features on the scheduler instance
    // Note: scheduleMenu and eventMenu are NOT disabled here because custom context
    // menus are wired up via scheduler.on() listeners in separate useEffect hooks
    const headerFeatureNames = ['timeAxisHeaderMenu', 'headerMenu', 'cellMenu', 'contextMenu'] as const;
    for (const featureName of headerFeatureNames) {
      const feature = (scheduler.features as unknown as Record<string, unknown>)[featureName] as { disabled?: boolean; menu?: { destroy?: () => void } } | undefined;
      if (feature && typeof feature === 'object') {
        feature.disabled = true;
        // Destroy any already-created menu widget
        if (feature.menu && typeof feature.menu.destroy === 'function') {
          feature.menu.destroy();
        }
      }
    }

    // Block contextmenu at the document level for all header-related elements
    // This catches the event before Bryntum can process it
    const globalHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target?.closest('.b-grid-headers, .b-sch-header-row, .b-sch-timeaxiscolumn, .b-sch-header-timeaxis-cell, .b-grid-header-scroller, .b-grid-header-container, .b-timeaxis-cell, .b-sch-header')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener('contextmenu', globalHandler, true);

    return () => {
      document.removeEventListener('contextmenu', globalHandler, true);
    };
  }, []);

  // Custom context menu for empty schedule cells
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    let contextMenu: Menu | null = null;

    const handleScheduleContextMenu = (event: { resourceRecord?: { id: string }; date?: Date; domEvent: MouseEvent }) => {
      // Prevent default browser context menu
      event.domEvent.preventDefault();

      // Only show menu for resource rows (not group rows)
      if (!event.resourceRecord || !event.date) return;

      // Check if this is a group row (has children)
      const record = event.resourceRecord as unknown as { children?: unknown[] };
      if (record.children && record.children.length > 0) return;

      // Also check if this is a group node (group-, project-, role-, or customer- prefix)
      const resourceId = event.resourceRecord.id;
      if (resourceId.startsWith('group-') || resourceId.startsWith('project-') || resourceId.startsWith('role-') || resourceId.startsWith('customer-')) return;
      const clickDate = event.date;

      // Check if this is a non-working day for the resource - disable context menu if so
      const resource = findResourceById(resourceId);
      if (resource && isNonWorkingDay(resource, clickDate)) {
        // Don't show context menu on non-working days
        return;
      }

      // Check if this is during non-business hours - disable context menu if so (Day view)
      if (isNonBusinessHour(clickDate)) {
        return;
      }

      // Destroy previous menu if exists
      if (contextMenu) {
        contextMenu.destroy();
      }

      // SVG icons for menu items
      const addIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>';
      const boltIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>';
      const pasteIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>';

      // Build menu items - only include Paste Booking if a booking is copied
      const menuItems = [
        {
          html: `<div style="display: flex; align-items: center; gap: 8px;">${addIcon}<span>Add Booking</span></div>`,
          onItem: () => {
            const resource = findResourceById(resourceId);
            if (resource && onAddBooking) {
              // In project-resource mode, pass the composite Bryntum ID so the handler
              // can extract the project ID for pre-populating the booking form
              if (groupingMode === 'project-resource' && resourceId.includes('-') &&
                !resourceId.startsWith('group-') && !resourceId.startsWith('project-')) {
                onAddBooking({ ...resource, id: resourceId }, clickDate);
              } else {
                onAddBooking(resource, clickDate);
              }
            }
          },
        },
        {
          html: `<div style="display: flex; align-items: center; gap: 8px;">${boltIcon}<span>Quick Add Booking</span></div>`,
          onItem: () => {
            const resource = findResourceById(resourceId);
            if (resource && onQuickAddBooking) {
              // In project-resource mode, pass the composite Bryntum ID so the handler
              // can extract the project ID for pre-populating the booking
              if (groupingMode === 'project-resource' && resourceId.includes('-') &&
                !resourceId.startsWith('group-') && !resourceId.startsWith('project-')) {
                onQuickAddBooking({ ...resource, id: resourceId }, clickDate);
              } else {
                onQuickAddBooking(resource, clickDate);
              }
            }
          },
        },
      ];

      // Only add Paste Booking if a booking has been copied
      if (copiedBooking) {
        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;">${pasteIcon}<span>Paste Booking</span></div>`,
          onItem: () => {
            const resource = findResourceById(resourceId);
            if (resource && onPasteBooking && copiedBooking) {
              const startTime = { hour: clickDate.getHours(), minute: clickDate.getMinutes() };
              onPasteBooking(resource, clickDate, startTime, copiedBooking);
            }
          },
        });
      }

      // Create custom context menu with smaller styling
      contextMenu = new Menu({
        anchor: true,
        autoShow: false,
        cls: 'custom-booking-menu',
        items: menuItems,
      });

      // Show menu at mouse position
      contextMenu.showBy([event.domEvent.clientX, event.domEvent.clientY]);
    };

    // Add listener for schedule context menu - use the detacher function returned by on()
    const detacher = scheduler.on('scheduleContextMenu', handleScheduleContextMenu);

    return () => {
      // Use the detacher function to remove the listener
      if (typeof detacher === 'function') {
        detacher();
      }
      if (contextMenu) {
        contextMenu.destroy();
      }
    };
  }, [findResourceById, onAddBooking, onQuickAddBooking, onPasteBooking, copiedBooking, isNonWorkingDay, isNonBusinessHour, groupingMode]);

  // Custom context menu for events (bookings)
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    let eventContextMenu: Menu | null = null;

    const handleEventContextMenu = (event: { eventRecord?: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown }; domEvent: MouseEvent }) => {
      // Prevent default browser context menu
      event.domEvent.preventDefault();

      if (!event.eventRecord) return;

      // Try multiple ways to access the booking data (Bryntum stores data differently)
      const eventRecord = event.eventRecord;
      let booking = eventRecord.data?.originalBooking;

      // Fallback: try originalData
      if (!booking && eventRecord.originalData) {
        booking = eventRecord.originalData.originalBooking;
      }

      // Fallback: try get() method
      if (!booking && typeof eventRecord.get === 'function') {
        booking = eventRecord.get('originalBooking') as Booking | undefined;
      }

      // If still no booking, try to find it by ID
      if (!booking) {
        const eventId = eventRecord.data?.id || (eventRecord as unknown as { id?: string }).id;
        if (eventId) {
          booking = bookings.find(b => String(b.id) === String(eventId));
        }
      }

      if (!booking) {
        console.warn('Could not find booking for event context menu');
        return;
      }

      // Destroy previous menu if exists
      if (eventContextMenu) {
        eventContextMenu.destroy();
      }

      // SVG icons for event menu items
      const editIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
      const copyIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
      const moveIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>';
      const trashIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';
      const statusIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>';
      const checkIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>';
      const chevronIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>';

      // Filter to only show enabled statuses
      const enabledStatuses = statusConfigs.filter(status => status.enabled);

      // Build menu items for event context menu
      const menuItems: Array<{
        html: string;
        onItem?: () => void;
        menu?: Array<{
          html: string;
          onItem: () => void;
        }>;
      }> = [
          {
            html: `<div style="display: flex; align-items: center; gap: 8px;">${editIcon}<span style="font-size: 12px; font-weight: 400;">Edit Booking</span></div>`,
            onItem: () => {
              if (onEditBooking) {
                onEditBooking(booking);
              }
            },
          },
          {
            html: `<div style="display: flex; align-items: center; gap: 8px;">${copyIcon}<span style="font-size: 12px; font-weight: 400;">Copy Booking</span></div>`,
            onItem: () => {
              if (onCopyBooking) {
                onCopyBooking(booking);
              }
            },
          },
          {
            html: `<div style="display: flex; align-items: center; gap: 8px;">${moveIcon}<span style="font-size: 12px; font-weight: 400;">Move</span></div>`,
            onItem: () => {
              if (onMoveBooking) {
                onMoveBooking(booking);
              }
            },
          },
        ];

      // Add Status submenu if there are enabled statuses and handler is provided
      if (onStatusChange && enabledStatuses.length > 0) {
        const statusMenuItems = enabledStatuses.map((status) => {
          const displayText = status.customText || status.text;
          const isCurrentStatus = booking.status === status.id;
          const colorCircle = `<div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${status.color}; flex-shrink: 0;"></div>`;
          const checkmark = isCurrentStatus ? checkIcon : '<span style="width: 14px; display: inline-block;"></span>';

          return {
            html: `<div style="display: flex; align-items: center; gap: 8px;">${colorCircle}<span style="font-size: 12px; ${isCurrentStatus ? 'font-weight: 600;' : 'font-weight: 400;'}">${displayText}</span>${checkmark}</div>`,
            onItem: () => {
              if (onStatusChange) {
                onStatusChange(booking, String(status.id), status.color);
              }
            },
          };
        });

        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;">${statusIcon}<span style="font-size: 12px; font-weight: 400;">Change Status</span>${chevronIcon}</div>`,
          menu: statusMenuItems,
        });
      }

      // Add Delete at the end
      menuItems.push({
        html: `<div style="display: flex; align-items: center; gap: 8px;">${trashIcon}<span style="font-size: 12px; font-weight: 400;">Delete Booking</span></div>`,
        onItem: () => {
          if (onDeleteBooking) {
            onDeleteBooking(booking);
          }
        },
      });

      // Create custom context menu with smaller styling
      eventContextMenu = new Menu({
        anchor: true,
        autoShow: false,
        cls: 'custom-booking-menu',
        items: menuItems,
      });

      // Show menu at mouse position
      eventContextMenu.showBy([event.domEvent.clientX, event.domEvent.clientY]);
    };

    // Add listener for event context menu
    const detacher = scheduler.on('eventContextMenu', handleEventContextMenu);

    return () => {
      if (typeof detacher === 'function') {
        detacher();
      }
      if (eventContextMenu) {
        eventContextMenu.destroy();
      }
    };
  }, [onEditBooking, onCopyBooking, onMoveBooking, onDeleteBooking, onStatusChange, statusConfigs, bookings]);

  // Handle expand/collapse for group rows - using callback handler
  const handleCellClick = useCallback((event: { record?: { children?: unknown[]; expanded?: boolean; isParent?: boolean; childLevel?: number }; column?: { type?: string }; source?: { resourceStore?: { toggleCollapse?: (record: unknown) => void } } }) => {
    const record = event.record;

    // Check if this is a parent/group row (has children or childLevel is 0)
    const isGroupRow = record && (
      (record.children && (record.children as unknown[]).length > 0) ||
      record.isParent ||
      record.childLevel === 0
    );

    // Only handle clicks on tree column for group rows
    if (event.column?.type === 'tree' && isGroupRow) {
      // Try to toggle using the store's toggleCollapse method
      const store = event.source?.resourceStore;
      if (store && typeof store.toggleCollapse === 'function') {
        store.toggleCollapse(record);
      } else {
        // Fallback: try to access the scheduler instance directly
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const resourceStore = scheduler.resourceStore as unknown as { toggleCollapse?: (record: unknown) => void };
          if (resourceStore && typeof resourceStore.toggleCollapse === 'function') {
            resourceStore.toggleCollapse(record);
          }
        }
      }
    }
  }, []);

  const columns = useMemo(() => {
    // Expand/collapse icon: minus-square when expanded, plus-square when collapsed
    const expandCollapseSquareIcon = allGroupsExpanded
      ? `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`
      : `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;

    return [
    {
      type: 'tree',
      text: '',
      field: 'name',
      width: 260,
      htmlEncode: false,
      editor: false,
      editable: false,
      sortable: false,
      // Prevent Bryntum's JS from calculating or applying any tree indent.
      // CSS overrides alone don't survive re-mounts because Bryntum re-runs
      // its row rendering pipeline which re-applies padding-inline-start via
      // JS after our stylesheet rules are resolved. indentSize: 0 stops it
      // at the source — no indent is ever written to the DOM.
      indentSize: 0,
      headerRenderer: () => {
        // Only the expand/collapse button lives here — the grouping trigger is a
        // React overlay (see the .grouping-header-overlay div in the JSX return)
        // so it is never subject to Bryntum header click interception.
        return `
          <div style="display: flex; align-items: center; justify-content: flex-end; width: 100%; padding: 0 12px 0 0; height: 100%;">
            <button
              id="expand-collapse-all-btn"
              title="${allGroupsExpanded ? 'Collapse all' : 'Expand all'}"
              style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                width: 32px;
                height: 32px;
                border: 1px solid hsl(var(--border));
                border-radius: 6px;
                background: transparent;
                color: hsl(var(--foreground));
                cursor: pointer;
                transition: background 0.15s, border-color 0.15s;
              "
              onmouseover="this.style.background='hsl(var(--accent))'; this.style.borderColor='hsl(var(--ring))';"
              onmouseout="this.style.background='transparent'; this.style.borderColor='hsl(var(--border))';"
            >
              ${expandCollapseSquareIcon}
            </button>
          </div>
        `;
      },
      renderer: ({ record, cellElement }: { record: { id?: string; name: string; role?: string; imageUrl?: string; children?: unknown[]; expanded?: boolean; isParent?: boolean; data?: { id?: string; name?: string; role?: string; imageUrl?: string; children?: unknown[]; isProjectGroup?: boolean; projectColor?: string }; get?: (field: string) => unknown }; cellElement?: HTMLElement }) => {
        // Access data - Bryntum may store it in record.data or directly on record
        const getData = (field: string): string | boolean | undefined => {
          if (record.get && typeof record.get === 'function') {
            return record.get(field) as string | boolean | undefined;
          }
          if (record.data && field in record.data) {
            return (record.data as unknown as Record<string, unknown>)[field] as string | boolean | undefined;
          }
          return (record as unknown as Record<string, unknown>)[field] as string | boolean | undefined;
        };

        const id = getData('id') as string || record.id || '';
        const name = getData('name') as string || record.name || '';
        const role = getData('role') as string || record.role;
        const imageUrl = getData('imageUrl') as string || record.imageUrl;
        const children = record.children || record.data?.children;
        const isExpanded = record.expanded;
        const isParent = record.isParent || (children && (children as unknown[]).length > 0);
        const isProjectGroup = getData('isProjectGroup') as boolean || record.data?.isProjectGroup;
        const projectColor = getData('projectColor') as string || record.data?.projectColor;

        // Apply alternate row shading: add class to every other leaf row,
        // skip group-header (parent) rows entirely so striping stays consistent.
        // We apply the class to ALL rows sharing the same data-id so that both
        // the locked (resource column) and normal (timeline) subgrids are shaded.
        if (cellElement) {
          const rowEl = cellElement.closest('.b-grid-row') as HTMLElement | null;
          if (rowEl) {
            const rowDataId = rowEl.getAttribute('data-id');
            const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
            const schedulerEl = scheduler && !scheduler.isDestroyed
              ? (scheduler as any).element as HTMLElement | undefined
              : undefined;

            // Collect all row elements across subgrids that belong to this record
            const allRowEls: HTMLElement[] = [];
            if (schedulerEl && rowDataId) {
              schedulerEl.querySelectorAll(`.b-grid-row[data-id="${rowDataId}"]`).forEach((el) => {
                allRowEls.push(el as HTMLElement);
              });
            }
            if (allRowEls.length === 0) {
              allRowEls.push(rowEl);
            }

            if (isParent) {
              allRowEls.forEach((el) => el.removeAttribute('data-alt-shading'));
            } else {
              // Compute this leaf's index among all visible leaf records
              if (scheduler && !scheduler.isDestroyed) {
                const records = (scheduler.resourceStore as any).records || [];
                let leafIdx = 0;
                for (const rec of records) {
                  if (String(rec.id) === String(record.id ?? id)) break;
                  if (!rec.isParent) leafIdx++;
                }
                const shouldShade = leafIdx % 2 === 0;
                allRowEls.forEach((el) => {
                  if (shouldShade) {
                    el.setAttribute('data-alt-shading', 'true');
                  } else {
                    el.removeAttribute('data-alt-shading');
                  }
                });
              }
            }
          }
        }

        // Group header row - show arrow icon and name (smaller text)
        if (isParent) {
          const arrowIcon = isExpanded
            ? `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="min-width: 12px;"><polyline points="6 9 12 15 18 9"></polyline></svg>`
            : `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="min-width: 12px;"><polyline points="9 18 15 12 9 6"></polyline></svg>`;

          // Project group - show with color indicator and project code
          if (isProjectGroup && projectColor) {
            const projectCode = getData('projectCode') as string || (record as any).data?.projectCode;

            return `
                          <div style="display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 12px; cursor: pointer;">
                            ${arrowIcon}
                            <div style="width: 10px; height: 10px; border-radius: 3px; background-color: ${projectColor}; flex-shrink: 0;"></div>
                            <div style="display: flex; flex-direction: column; gap: 2px;">
                              <span style="line-height: 1.2;">${name}</span>
                              ${projectCode ? `<span style="font-size: 10px; font-weight: 400; opacity: 0.7; line-height: 1.2;">${projectCode}</span>` : ''}
                            </div>
                          </div>
                        `;
          }

          // Regular department/scheduler group
          return `
                        <div style="display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 12px; cursor: pointer;">
                          ${arrowIcon}
                          <span>${name}</span>
                        </div>
                      `;
        }
        // Resource row - show profile image or teal-gradient initials fallback, name, and role
        const initials = (() => {
          const parts = name.trim().split(/\s+/);
          if (parts.length === 0) return '?';
          const first = parts[0]?.[0] || '';
          const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
          return (first + last).toUpperCase();
        })();
        const hasPhoto = !!imageUrl && imageUrl.trim() !== '' && imageUrl !== 'null' && imageUrl !== 'undefined';

        // Determine utilization value for this row (handles combined IDs)
        // Read from ref to always get the latest values, even if Bryntum
        // has cached this renderer closure from a previous render cycle
        const currentUtilMap = resourceUtilizationMapRef.current;
        let utilization = currentUtilMap.get(String(id));
        if (utilization === undefined) {
          if (id && id.includes('-') && !id.startsWith('group-') && !id.startsWith('role-') && !id.startsWith('project-') && !id.startsWith('customer-')) {
            const parts = id.split('-');
            if (parts.length >= 2) {
              const actualResourceId = parts.slice(1).join('-');
              utilization = currentUtilMap.get(String(actualResourceId));
            }
          }
        }
        const utilValue = typeof utilization === 'number' ? utilization : 0;
        const progressColor = getUtilizationColorCapped(utilValue, settings.utilisationTiers);

        // Build working hours tooltip from global or custom profile (demo-visible change)
        const resObj = findResourceById(id);
        const profile = (resObj && resObj.useCustomWorkingProfile && resObj.customWorkingProfile)
          ? resObj.customWorkingProfile
          : settings.globalWorkingProfile;
        const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
        const parts: string[] = [];
        for (const day of daysOrder) {
          const hours = (profile?.[day] ?? 0) as number;
          if (hours > 0) {
            const label = day === 'monday' ? 'Mon' : day === 'tuesday' ? 'Tue' : day === 'wednesday' ? 'Wed' : day === 'thursday' ? 'Thu' : day === 'friday' ? 'Fri' : day === 'saturday' ? 'Sat' : 'Sun';
            parts.push(`${label}: ${hours}h`);
          }
        }
        const header = (resObj && resObj.useCustomWorkingProfile && resObj.customWorkingProfile) ? 'Working Hours (Custom Profile)' : 'Working Hours';
        const tooltipText = parts.length ? `${header}: ${parts.join(', ')}` : 'No working hours configured';
        const safeTooltip = tooltipText.replace(/\"/g, '&quot;').replace(/\n/g, ' ');

        // Derive badge colors from progressColor for utilisation badge.
        // Background/border use a light tint of the tier colour; text is always
        // dark so it remains readable against the pastel background.
        const hexToRgb = (hex: string) => {
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 107, g: 114, b: 128 };
        };
        const rgb = hexToRgb(progressColor);
        const utilBgColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
        const utilTextColor = `rgba(0, 0, 0, 0.8)`;
        const utilBorderColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;

        return `
                      <div style="display: flex; align-items: center; gap: 6px; width: 100%; min-width: 0;">
                        <div style="position: relative; flex-shrink: 0;" title="${utilValue}% Utilisation&#10;${safeTooltip}">
                          ${hasPhoto
                            ? `<img src="${imageUrl}" alt="${name}" style="width: 30px; height: 30px; border-radius: 6px; object-fit: cover; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" /><div style="width: 30px; height: 30px; border-radius: 6px; display: none; align-items: center; justify-content: center; background: oklch(50% .0896 194.8); color: rgba(255,255,255,0.9); font-weight: 600; font-size: 11px; letter-spacing: 0.5px;">${initials}</div>`
                            : `<div style="width: 30px; height: 30px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: oklch(50% .0896 194.8); color: rgba(255,255,255,0.9); font-weight: 600; font-size: 11px; letter-spacing: 0.5px;">${initials}</div>`
                          }
                        </div>
                        <div style="flex: 1; min-width: 0; overflow: hidden;">
                          <div style="font-weight: 500; font-size: 12px; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${name}</div>
                          ${role ? `<div style="font-size: 10px; opacity: 0.7; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${role}</div>` : ''}
                        </div>
                        <div style="
                          display: inline-flex; align-items: center; justify-content: center;
                          min-width: 34px; padding: 2px 4px; border-radius: 8px;
                          font-size: 10px; font-weight: 600; line-height: 1.3;
                          background: ${utilBgColor}; color: ${utilTextColor}; border: 1px solid ${utilBorderColor};
                          white-space: nowrap; flex-shrink: 0;
                        ">${utilValue}%</div>
                      </div>
                    `;
      },
    },
  ];
  }, [allGroupsExpanded, resourceUtilizationMap, settings.utilisationTiers]);

  const schedulerConfig = useMemo(() => {
    return {
      // viewPreset is intentionally NOT passed as a React prop.
      // Passing it here causes Bryntum's React wrapper to call setViewPreset()
      // outside of any suspendRefresh block, which destroys the time axis and
      // wipes the event store independently of our imperative store-sync effect.
      // The viewPreset is applied exclusively inside the store-sync useEffect
      // (within suspendRefresh/resumeRefresh) so the preset change, store
      // population and setTimeSpan are always a single atomic operation.
      barMargin: 1,
      rowHeight: 50,
      eventStyle: 'plain' as any,
      eventColor: 'blue',
      eventLayout: 'pack',
      eventRenderer: ({ eventRecord, renderData }: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown }; renderData: { eventColor: string; style: string } }) => {
        // Get the original booking to check for multi-resource
        const booking = eventRecord.data?.originalBooking ||
          eventRecord.originalData?.originalBooking ||
          (eventRecord.get ? eventRecord.get('originalBooking') as Booking | undefined : undefined);

        // ── What-if ghost tile ─────────────────────────────────────────────
        // Prevent Bryntum adding ANY color class (b-sch-event-blue etc.) by
        // blanking renderData.eventColor, then force transparent via style.
        if (eventRecord.data?.isWhatIfGhost) {
          (renderData as any).eventColor = 'none';
          // Force all visuals inline — inline styles always beat Bryntum's injected classes.
          // Use a flex column wrapper so child divs fill the full event height naturally.
          renderData.style = [
            'background:transparent',
            'background-color:transparent',
            'border:2px dashed rgba(13,148,136,0.85)',
            'border-radius:4px',
            'box-shadow:none',
            'overflow:visible',
            'position:relative',
            'animation:whatif-border-pulse 1.8s ease-in-out infinite',
          ].join(';');

          // SVG hatch fill — stretches to 100%×100% of whatever size Bryntum
          // gives the event element. SVG with preserveAspectRatio="none" is the
          // only reliable way to fill an unknown-height container from innerHTML.
          // Three pattern layers: -45°, +45° and horizontal dashes.
          const hatch =
            '<svg xmlns="http://www.w3.org/2000/svg" style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;" preserveAspectRatio="none">' +
              '<defs>' +
                '<pattern id="wif-diag1" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(-45)">' +
                  '<line x1="0" y1="0" x2="0" y2="10" stroke="rgba(13,148,136,0.35)" stroke-width="1.5"/>' +
                '</pattern>' +
                '<pattern id="wif-diag2" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(45)">' +
                  '<line x1="0" y1="0" x2="0" y2="10" stroke="rgba(13,148,136,0.18)" stroke-width="1"/>' +
                '</pattern>' +
                '<pattern id="wif-horiz" patternUnits="userSpaceOnUse" width="10" height="12">' +
                  '<line x1="0" y1="0" x2="10" y2="0" stroke="rgba(13,148,136,0.28)" stroke-width="1.5"/>' +
                '</pattern>' +
              '</defs>' +
              '<rect width="100%" height="100%" fill="url(#wif-diag1)"/>' +
              '<rect width="100%" height="100%" fill="url(#wif-diag2)"/>' +
              '<rect width="100%" height="100%" fill="url(#wif-horiz)"/>' +
            '</svg>';

          // Centred "Proposed" pill — sits above the SVG hatch.
          const pill =
            '<div style="' +
              'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2;' +
              'font-size:11px;font-weight:700;letter-spacing:0.06em;white-space:nowrap;' +
              'color:#0f766e;background:rgba(255,255,255,0.92);' +
              'border:1.5px solid rgba(13,148,136,0.65);' +
              'padding:2px 9px;border-radius:12px;' +
              'box-shadow:0 1px 4px rgba(0,0,0,0.15);pointer-events:none;">' +
              '◈  Proposed' +
            '</div>';

          return hatch + pill;
        }

        // Force dark text on ALL events (heatmap + booking) via renderData.style.
        // This is the only reliable way to override Bryntum's internal colour
        // logic which defaults to white via --primary-foreground.
        renderData.style = (renderData.style || '') + '; color: rgba(0,0,0,0.75) !important;';

        // Check if this is a heatmap event — exit early after applying text colour.
        if (eventRecord.data?.isHeatmapEvent) {
          return eventRecord.data?.name || '';
        }

        if (!booking) {
          return eventRecord.data?.name || 'Booking';
        }

        const bookingResourceIds = booking.resourceIds || (booking.resourceId ? [booking.resourceId] : []);
        const isMultiResource = bookingResourceIds.length > 1;

        // Build the event content HTML
        const usersIcon = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>';
        const multiResourceBadge = isMultiResource
          ? `<span style="display: inline-flex; align-items: center; gap: 2px; background: rgba(0,0,0,0.1); border-radius: 3px; padding: 0 3px; flex-shrink: 0; margin-left: 4px;" title="Multi-resource booking (${bookingResourceIds.length} resources)">${usersIcon}<span style="font-size: 10px; line-height: 1;">${bookingResourceIds.length}</span></span>`
          : '';

        return `<div style="display: flex; align-items: center; overflow: hidden; white-space: nowrap;"><span style="font-weight: 600; overflow: hidden; text-overflow: ellipsis;">${eventRecord.data?.name || 'Booking'}</span>${multiResourceBadge}</div>`;
      },
      snap: true,
      createEventOnDblClick: false,
      zoomOnMouseWheel: false,
      zoomOnTimeAxisDoubleClick: false,
      readOnly: isHeatmapMode,
      infiniteScroll: false,
      // Feature props — Bryntum React wrapper accepts each feature as
      // a top-level prop with a "Feature" suffix instead of nesting
      // them inside a `features` object.
      treeFeature: {
        expandOnCellClick: true,
      },
      rowCheckboxesFeature: false,
      cellEditFeature: false,
      quickFindFeature: false,
      filterFeature: false,
      sortFeature: false,
      columnReorderFeature: false,
      columnResizeFeature: false,
      eventDragFeature: isHeatmapMode ? false : {
        constrainDragToResource: false,
        showExactDropPosition: true,
        snapToTimeResolution: true,
        unifiedDrag: true,
      },
      eventResizeFeature: isHeatmapMode ? false : {
        showExactResizePosition: true,
        snapToTimeResolution: true,
      },
      eventEditFeature: false,
      eventTooltipFeature: {
        allowOver: true,
        hideDelay: 300,
        // Constrain the tooltip inside the viewport so it never gets clipped
        // when hovering over rows near the top/left/right edges.
        // 'b-t' means "align tooltip bottom-centre to target top-centre" (show above),
        // but Bryntum auto-flips to below/left/right when it doesn't fit.
        align: 'b-t',
        anchorToTarget: true,
        constrainTo: document.body,
        cls: 'b-heatmap-tooltip-popup',
        template: ({ eventRecord }: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown; id?: string } }) => {
          const hData = eventRecord.data?.heatmapData ||
            (eventRecord.get ? eventRecord.get('heatmapData') as BryntumEvent['heatmapData'] : null);
          if (hData) {
            const tickStart: Date = (eventRecord.data?.startDate as Date) || new Date();
            return generateHeatmapTooltipHtml(hData, tickStart);
          }

          const eventId = eventRecord.id || eventRecord.data?.id || (eventRecord.get ? eventRecord.get('id') as string : null);
          let booking: Booking | null = null;
          if (eventId) {
            booking = findBookingById(eventId) || null;
          }
          if (!booking) {
            booking = eventRecord.data?.originalBooking ||
              eventRecord.originalData?.originalBooking ||
              (eventRecord.get ? eventRecord.get('originalBooking') as Booking : null);
          }
          if (!booking) {
            return '<div style="padding: 8px;">No booking data</div>';
          }
          return generateTooltipHtml(booking);
        },
      },
      eventDragCreateFeature: isHeatmapMode ? false : {
        showTooltip: false,
      },
      // Disable all default context menus
      scheduleMenuFeature: false,
      cellMenuFeature: false,
      headerMenuFeature: false,
      contextMenuFeature: false,
      eventMenuFeature: false,
      timeAxisHeaderMenuFeature: false,
      nonWorkingTimeFeature: true,
      eventNonWorkingTimeFeature: true,
      timeRangesFeature: {
        enableResizing: false,
        showCurrentTimeLine: false,
        showHeaderElements: false,
      },
      // Enable resource time ranges for absences and bank holidays
      resourceTimeRangesFeature: true,
      // Disable schedule tooltip for empty cells (shows incorrect dates in Year/Quarter views)
      scheduleTooltipFeature: false,
    };
  }, [viewPreset, generateTooltipHtml, generateHeatmapTooltipHtml, findBookingById, calendarView, isHeatmapMode]);

  useEffect(() => {
    const applyTooltipTemplate = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;
      if (scheduler.features.eventTooltip) {
        const eventTooltipFeature = scheduler.features.eventTooltip as { template?: (data: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown; id?: string } }) => string; allowOver?: boolean; hideDelay?: number; align?: string; anchorToTarget?: boolean; constrainTo?: Element | null };
        eventTooltipFeature.allowOver = true;
        eventTooltipFeature.hideDelay = 300;
        // Ensure tooltip stays within the viewport and auto-flips when near edges
        eventTooltipFeature.align = 'b-t';
        eventTooltipFeature.anchorToTarget = true;
        eventTooltipFeature.constrainTo = document.body;
        eventTooltipFeature.template = ({ eventRecord }: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown; id?: string } }) => {
          try {
            const hData = eventRecord.data?.heatmapData ||
              (eventRecord.get ? eventRecord.get('heatmapData') as BryntumEvent['heatmapData'] : null);
            if (hData) {
              const tickStart: Date = (eventRecord.data?.startDate as Date) || new Date();
              return generateHeatmapTooltipHtmlRef.current(hData, tickStart);
            }
            const eventId = eventRecord.id || eventRecord.data?.id || (eventRecord.get ? eventRecord.get('id') as string : null);
            let booking: Booking | null = null;
            if (eventId) {
              booking = findBookingByIdRef.current(eventId) || null;
            }
            if (!booking) {
              booking = eventRecord.data?.originalBooking ||
                eventRecord.originalData?.originalBooking ||
                (eventRecord.get ? eventRecord.get('originalBooking') as Booking : null);
            }
            if (!booking) {
              return '<div style="padding: 8px;">No booking data</div>';
            }
            return generateTooltipHtmlRef.current(booking);
          } catch (err) {
            console.error('Tooltip template error:', err);
            return '<div style="padding: 8px;">Error loading tooltip</div>';
          }
        };
      }
    };
    applyTooltipTemplate();
    const timerId = setTimeout(applyTooltipTemplate, 100);
    return () => clearTimeout(timerId);
  }, [generateTooltipHtml, generateHeatmapTooltipHtml, findBookingById]);


  useEffect(() => {
    const overrideDragCreateTooltip = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;
      const dragCreate = scheduler.features.eventDragCreate as {
        showTooltip?: boolean;
        tooltipTemplate?: (data: { startDate: Date; endDate: Date; startClockHtml: string; endClockHtml: string }) => string;
        tip?: { html?: string };
      } | undefined;
      if (dragCreate) {
        dragCreate.tooltipTemplate = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
          const clockSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
          const startStr = format(startDate, 'HH:mm');
          const endStr = format(endDate, 'HH:mm');
          return `<div style=\"padding: 8px; font-size: 13px; line-height: 1.8;\">\n            <div style=\"display: flex; align-items: center; gap: 6px;\">${clockSvg} ${startStr}</div>\n            <div style=\"display: flex; align-items: center; gap: 6px;\">${clockSvg} ${endStr}</div>\n          </div>`;
        };
      }
    };
    overrideDragCreateTooltip();
    const timerId = setTimeout(overrideDragCreateTooltip, 200);
    return () => clearTimeout(timerId);
  }, []);



  // When groupingMode changes, force-refresh the scheduler's resource and event stores
  // so the new tree structure and resourceId mappings take effect without a full remount.
  useEffect(() => {
    const sync = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return false;

      const eventsData = isHeatmapMode ? heatmapEvents : bryntumEvents;

      // Suspend ALL Bryntum rendering for the entire operation so the engine
      // never sees a partial state (empty store between removeAll and add).
      scheduler.suspendRefresh();
      try {
        // Repopulate all stores inside the suspend block.
        scheduler.resourceStore.removeAll();
        scheduler.resourceStore.add(bryntumResources as any);

        // Imperatively apply dynamic rowHeight on leaf resource records.
        // Setting rowHeight as a plain data property is not picked up by
        // Bryntum's tree model — we must call set() on the model instance.
        scheduler.resourceStore.forEach((record: any) => {
          if (record.data?.rowHeight && !record.isParent) {
            record.set('rowHeight', record.data.rowHeight);
          }
        });

        scheduler.eventStore.removeAll();
        scheduler.eventStore.add(eventsData as any);

        // ── What-if ghost tile injection ──────────────────────────────────────
        // Injected here — inside suspendRefresh, after the regular events are
        // loaded — so it is atomic with the store population and fires exactly
        // when the scheduler is confirmed ready (no setTimeout guesswork).
        if (whatIfScenario && whatIfScenario.type === 'rebalance' && whatIfScenario.candidateResourceId) {
          const {
            candidateResourceId,
            atRiskBookingIds,
            atRiskResourceId,
            projectName,
            affectedWeekStarts,
            atRiskBookingDates,
          } = whatIfScenario;

          // Build ghost event(s) sized to the actual booking span when available,
          // falling back to a Mon 09:00–17:00 block per affected week.
          let ghostEvents: object[];
          if (atRiskBookingDates && atRiskBookingDates.length > 0) {
            ghostEvents = atRiskBookingDates.map((bd, idx) => ({
              id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
              resourceId: String(candidateResourceId),
              name: '',
              startDate: new Date(bd.startDate),
              endDate:   new Date(bd.endDate),
              cls: 'whatif-ghost-proposed',
              isWhatIfGhost: true,
              eventStyle: 'hollow',
            }));
          } else {
            const weeks = affectedWeekStarts && affectedWeekStarts.length > 0
              ? affectedWeekStarts
              : [whatIfScenario.targetWeekStart];
            ghostEvents = weeks.map((ws, idx) => {
              const dayStart = new Date(ws);
              dayStart.setHours(9, 0, 0, 0);
              const dayEnd = new Date(ws);
              dayEnd.setHours(17, 0, 0, 0);
              return {
                id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
                resourceId: String(candidateResourceId),
                name: '',
                startDate: dayStart,
                endDate: dayEnd,
                cls: 'whatif-ghost-proposed',
                isWhatIfGhost: true,
                eventStyle: 'hollow',
              };
            });
          }
          scheduler.eventStore.add(ghostEvents as any);

          // Apply whatif-moving class to the at-risk source booking tiles.
          if (atRiskBookingIds && atRiskBookingIds.length > 0) {
            atRiskBookingIds.forEach((bid) => {
              const rec = scheduler.eventStore.getById(bid);
              if (rec) {
                const existing: string = (rec as any).data?.cls ?? '';
                if (!existing.includes(MOVING_CLS)) {
                  rec.set('cls', [existing, MOVING_CLS].filter(Boolean).join(' '));
                }
              }
            });
          } else if (atRiskResourceId) {
            ((scheduler.eventStore as any).allRecords ?? []).forEach((rec: any) => {
              const ob = rec.data?.originalBooking;
              if (!ob) return;
              if (
                String(ob.resourceId) !== String(atRiskResourceId) ||
                (projectName && ob.projectName !== projectName)
              ) return;
              const existing: string = rec.data?.cls ?? '';
              if (!existing.includes(MOVING_CLS)) {
                rec.set('cls', [existing, MOVING_CLS].filter(Boolean).join(' '));
              }
            });
          }
        }

        // Re-populate the resourceTimeRangeStore — the React wrapper only applies
        // the resourceTimeRanges prop on mount so we keep it in sync manually.
        const rtrs = (scheduler as any).resourceTimeRangeStore;
        if (rtrs) {
          rtrs.removeAll();
          rtrs.add(resourceTimeRanges as any);
        }

        // Re-populate the timeRangeStore for global time ranges (non-working
        // hours in Day view etc.).
        const trs = (scheduler as any).timeRangeStore;
        if (trs) {
          trs.removeAll();
          trs.add(timeRanges as any);
        }

        // Apply the current viewPreset imperatively inside the suspend block.
        // viewPreset is a config property (getter/setter) on Bryntum's
        // TimelineBase, NOT a method — assign it directly.  It is NOT passed
        // as a React prop (see comment above schedulerConfig) because the
        // wrapper would call its own setter outside suspendRefresh and wipe
        // the event store.  Assigning it here keeps it atomic with the store
        // population.
        (scheduler as any).viewPreset = viewPreset;

        // Keep the time span in sync for same-view date navigation (next/prev
        // arrows).  View changes in heatmap mode are handled by a full remount
        // (see schedulerKey) so this path is only reached when the view itself
        // has not changed.
        if (startDate && endDate && startDate < endDate) {
          (scheduler as any).setTimeSpan(startDate, endDate);
        }
      } finally {
        scheduler.resumeRefresh(true);
      }
      return true;
    };

    // On a fresh remount (view change in heatmap mode) the Bryntum React wrapper
    // may not have attached the instance to schedulerRef yet when this effect
    // first fires.  Retry after a short delay so the store is always in sync
    // even if the ref attaches asynchronously.
    if (!sync()) {
      const t = setTimeout(() => sync(), 50);
      return () => clearTimeout(t);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupingMode, bryntumResources, bryntumEvents, heatmapEvents, isHeatmapMode, startDate, endDate, resourceTimeRanges, timeRanges, viewPreset, whatIfScenario]);


  // Atomically update the scheduler's visible date range using setTimeSpan.
  // This prevents the "Invalid start/end dates" error that occurs when the
  // BryntumScheduler React wrapper applies startDate and endDate as individual
  // prop changes — if endDate is set before startDate (or vice versa) during a
  // large date jump (e.g. clicking "Today" after navigating to another year),
  // the Bryntum engine sees a transient state where start > end and throws.
  useLayoutEffect(() => {
    // Handles same-view date navigation (next/prev arrows, Today button).
    // View changes in heatmap mode cause a full remount (see schedulerKey) so
    // this effect will not fire during those transitions — the remounting
    // instance receives the correct dates as initial props directly.
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler || scheduler.isDestroyed) return;

    scheduler.setTimeSpan(startDate, endDate);
  }, [startDate, endDate]);

  // Show loading state while theme is loading
  if (!isThemeLoaded) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Loading theme...</p>
        </div>
      </div>
    );
  }

  // CRITICAL: Prevent rendering if dates are invalid
  if (!startDate || !endDate || startDate >= endDate) {
    console.error('🚨 CRITICAL: Cannot render scheduler with invalid dates:', {
      startDate: startDate?.toString(),
      endDate: endDate?.toString(),
      startTime: startDate?.getTime(),
      endTime: endDate?.getTime()
    });
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 text-destructive">⚠︝</div>
          <p className="text-sm text-destructive">Invalid date range. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  // Cast to any to bypass strict Bryntum library type incompatibilities
  const AnyBryntumScheduler = BryntumScheduler as any;

  return (
    <div className="h-full w-full bryntum-scheduler-container custom-style">

      <AnyBryntumScheduler
        key={schedulerKey}
        ref={schedulerRef as any}
        id={schedulerKey}
        startDate={initialDatesRef.current.startDate}
        endDate={initialDatesRef.current.endDate}
        viewPreset={initialViewPresetRef.current}
        resources={bryntumResources}
        events={isHeatmapMode ? heatmapEvents : bryntumEvents}
        resourceTimeRanges={resourceTimeRanges}
        timeRanges={timeRanges}
        columns={columns}
        {...schedulerConfig}
        onEventClick={handleEventClick}
        onEventDblClick={handleEventDblClick}
        onScheduleClick={handleScheduleClick}
        onScheduleDblClick={handleScheduleDblClick}
        onEventDrop={handleEventDrop}
        onEventResizeEnd={handleEventResizeEnd}
        onDragCreateEnd={handleDragCreateEnd}
        onCellClick={handleCellClick}
      />


    </div>
  );
});

BryntumSchedulerTimeline.displayName = 'BryntumSchedulerTimeline';
